import NonTranslatable from './non-translatable.ts';
import npmApiTranslations from './pl.api.npm.ts';
import pt9800ApiTranslations from './pl.api.pt9800.ts';

import {
  generalTerm,
} from '@/constants/locales/pl.shared.ts';
import {
  TValidateExpressionRouteResponse,
} from '@/generated/qmulus/axios/signal-builder.ts';
import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  EAppGroupSemanticDepartment, EAppGroupSemanticTopic, EAppId,
} from '@/types/app-type.ts';
import {
  EAssetProfileCombinator,
} from '@/types/asset-profile-combinator.ts';
import {
  EAssetProfileStatus,
} from '@/types/asset-profile-status.ts';
import {
  EHierarchy,
} from '@/types/asset-tree.ts';
import {
  EAuditResultType,
} from '@/types/audit-result.ts';
import {
  ECalendarEventType,
  ECalendarPeriod,
  EMaintenancePriority,
  EMaintenanceRecurrenceEndCondition,
  EMaintenanceRecurrenceMonthDayDimension,
  EMaintenanceRecurrenceMonthDayValue,
  EMaintenanceState,
} from '@/types/calendar.ts';
import {
  EChamberType, EHeatTreatmentProcessType,
} from '@/types/chamber.ts';
import {
  TFunctionCategory,
  TFunctionDefinition,
  TOperatorDefinition,
} from '@/types/function.ts';
import {
  TJobStatus,
} from '@/types/job-status.ts';
import {
  EBlobMeaning,
  EConsumptionMeaningMap, EJobFeatureMeaningMap,
  EPhysicalPropertyMeaningMap,
} from '@/types/meaning-type.ts';
import {
  EDataFormat,
} from '@/types/metadata.ts';
import {
  EModelLifecycleState,
} from '@/types/model-builder.ts';
import {
  ENotificationChannelType,
} from '@/types/notification-channel-type.ts';
import {
  ESubscriptionTreeLabel,
} from '@/types/notification.ts';
import {
  EObservationType,
} from '@/types/observation-type.ts';
import {
  ETimeseriesMeaning,
  TComponentKeys,
  TComponentValue,
  TComponentsSchema,
} from '@/types/openapi-type.ts';
import {
  TOrderStatus,
} from '@/types/order-status.ts';
import {
  EPhysicalDimension,
} from '@/types/physical-dimension.ts';
import {
  TQmulusDashboardWidgetSharedProps,
  TQmulusDashboardWidgetType,
} from '@/types/qmulus-dashboard.ts';
import {
  TReportBlockId,
} from '@/types/report-block.ts';
import {
  EScaleOption,
} from '@/types/scale.ts';
import {
  ESimulatorAMS275910CompoundLayerClass,
  ESimulatorAMS275910SteelClass,
  ESimulatorAMS275912PorosityClass,
  ESimulatorAMS275912SteelClass,
  ESimulatorPreTreatmentType,
  ESimulatorRecipeSimulationValuesType,
  ESimulatorRecipeType,
  ESimulatorSimulationPartShape,
} from '@/types/simulator.ts';
import {
  EUnitOfMeasurement,
} from '@/types/unit-of-measurement.ts';
import {
  sinkExpression, sinkVariable,
} from '@/utils/expression.ts';

const SparePart: Partial<TComponentKeys<'SparePart'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  manufacturerId: generalTerm.manufacturer,
  manufacturerDisplayName: generalTerm.manufacturer,
};

const HeatTreatmentCapability: Partial<TComponentKeys<'HeatTreatmentCapability'>> = {
  heatTreatmentProcess: generalTerm.heatTreatmentProcess,
  preference: generalTerm.preference,
};

const UnitOfMeasurementDisplayName: Record<EUnitOfMeasurement, string> = {
  [EUnitOfMeasurement.AMPERE]: 'Amper',
  [EUnitOfMeasurement.ATMOSPHERE]: 'Atmosfera',
  [EUnitOfMeasurement.BAR]: 'Bar',
  [EUnitOfMeasurement.BAR_PER_MINUTE]: 'Bar na minutę',
  [EUnitOfMeasurement.BRINELL_HARDNESS]: 'Twardość Brinella',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_10]: 'Twardość Brinella 10/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_15]: 'Twardość Brinella 10/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_10]: 'Twardość Brinella 10/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_15]: 'Twardość Brinella 10/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_10]: 'Twardość Brinella 10/500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_15]: 'Twardość Brinella 10/500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_10]: 'Twardość Brinella 6/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_15]: 'Twardość Brinella 6/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_10]: 'Twardość Brinella 6/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_15]: 'Twardość Brinella 6/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_10]: 'Twardość Brinella 6/500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_15]: 'Twardość Brinella 6/500/15',
  [EUnitOfMeasurement.BTU]: 'Brytyjska jednostka ciepła (BTU)',
  [EUnitOfMeasurement.BTU_PER_HOUR]: 'BTU na godzinę',
  [EUnitOfMeasurement.BTU_PER_MINUTE]: 'BTU na minutę',
  [EUnitOfMeasurement.CENTIMETRE_PER_MINUTE]: 'Centymetry na minutę',
  [EUnitOfMeasurement.CENTIMETRE_PER_SECOND]: 'Centymetry na sekundę',
  [EUnitOfMeasurement.CUBIC_CENTIMETRE_PER_MINUTE]: 'Centymetr sześcienny na minutę',
  [EUnitOfMeasurement.CUBIC_FEET]: 'Stopy sześcienne',
  [EUnitOfMeasurement.CUBIC_FEET_PER_HOUR]: 'Stopy sześcienne na godzinę',
  [EUnitOfMeasurement.CUBIC_FEET_PER_MINUTE]: 'Stopy sześcienne na minutę',
  [EUnitOfMeasurement.CUBIC_METRES]: 'Metry sześcienne',
  [EUnitOfMeasurement.CUBIC_METRE_PER_HOUR]: 'Metry sześcienne na godzinę',
  [EUnitOfMeasurement.CUBIC_METRE_PER_MINUTE]: 'Metry sześcienne na minutę',
  [EUnitOfMeasurement.CUBIC_METRE_PER_SECOND]: 'Metry sześcienne na sekundę',
  [EUnitOfMeasurement.DAY]: 'Dzień',
  [EUnitOfMeasurement.DEGREES_CELSIUS]: 'Stopnie Celsjusza',
  [EUnitOfMeasurement.DEGREES_CELSIUS_PER_MINUTE]: 'Stopnie Celsjusza na minutę',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT]: 'Stopnie Fahrenheita',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT_PER_MINUTE]: 'Stopnie Fahrenheita na minutę',
  [EUnitOfMeasurement.DEGREES_KELVIN]: 'Stopnie Kelvina',
  [EUnitOfMeasurement.DELTA_DEGREES_CELSIUS]: 'Różnica temperatury w stopniach Celsjusza',
  [EUnitOfMeasurement.DELTA_DEGREES_FAHRENHEIT]: 'Różnica temperatury w stopniach Fahrenheita',
  [EUnitOfMeasurement.DELTA_DEGREES_KELVIN]: 'Różnica temperatury w kelwinach',
  [EUnitOfMeasurement.FEET_OF_WATER_COLUMN]: 'Stopy słupa wody',
  [EUnitOfMeasurement.FEET_PER_MINUTE]: 'Stopy na minutę',
  [EUnitOfMeasurement.FEET_PER_SECOND]: 'Stopy na sekundę',
  [EUnitOfMeasurement.GALLONS_PER_HOUR]: 'Galony na godzinę',
  [EUnitOfMeasurement.GALLONS_PER_MINUTE]: 'Galony na minutę',
  [EUnitOfMeasurement.GRAM]: 'Gram',
  [EUnitOfMeasurement.GRAM_PER_SECOND]: 'Gram na sekundę',
  [EUnitOfMeasurement.HECTO_PASCAL]: 'Hektopaskal',
  [EUnitOfMeasurement.HERTZ]: 'Herc',
  [EUnitOfMeasurement.HORSEPOWER]: 'Konie mechaniczne',
  [EUnitOfMeasurement.HOUR]: 'Godzina',
  [EUnitOfMeasurement.INCH]: 'Cal',
  [EUnitOfMeasurement.INCH_OF_MERCURY]: 'Cale słupa rtęci',
  [EUnitOfMeasurement.INCH_OF_WATER_COLUMN]: 'Cale słupa wody',
  [EUnitOfMeasurement.INCH_PER_MINUTE]: 'Cale na minutę',
  [EUnitOfMeasurement.INCH_PER_SECOND]: 'Cale na sekundę',
  [EUnitOfMeasurement.KILOGRAM]: 'Kilogram',
  [EUnitOfMeasurement.KILOGRAM_PER_HOUR]: 'Kilogram na godzinę',
  [EUnitOfMeasurement.KILOGRAM_PER_MINUTE]: 'Kilogram na minutę',
  [EUnitOfMeasurement.KILOGRAM_PER_SECOND]: 'Kilogram na sekundę',
  [EUnitOfMeasurement.KILOMETER]: 'Kilometr',
  [EUnitOfMeasurement.KILOVOLT]: 'Kilowolt',
  [EUnitOfMeasurement.KILOVOLT_AMPERE]: 'Kilowoltamper',
  [EUnitOfMeasurement.KILOWATT]: 'Kilowat',
  [EUnitOfMeasurement.KILOWATT_HOUR]: 'Kilowatogodzina',
  [EUnitOfMeasurement.KILO_PASCAL]: 'Kilopaskal',
  [EUnitOfMeasurement.KNOOP_HARDNESS]: 'Twardość Knoopa',
  [EUnitOfMeasurement.KNOOP_HARDNESS_100]: 'Twardość Knoopa 100',
  [EUnitOfMeasurement.KNOOP_HARDNESS_200]: 'Twardość Knoopa 200',
  [EUnitOfMeasurement.KNOOP_HARDNESS_500]: 'Twardość Knoopa 500',
  [EUnitOfMeasurement.KNOOP_HARDNESS_50]: 'Twardość Knoopa 50',
  [EUnitOfMeasurement.LITRE]: 'Litr',
  [EUnitOfMeasurement.LITRE_PER_HOUR]: 'Litr na godzinę',
  [EUnitOfMeasurement.LITRE_PER_MINUTE]: 'Litr na minutę',
  [EUnitOfMeasurement.MEGAWATT]: 'Megawat',
  [EUnitOfMeasurement.METRES_PER_MINUTE]: 'Metry na minutę',
  [EUnitOfMeasurement.METRES_PER_SECOND]: 'Metry na sekundę',
  [EUnitOfMeasurement.METRE]: 'Metr',
  [EUnitOfMeasurement.MICROBAR]: 'Mikrobar',
  [EUnitOfMeasurement.MICROBAR_PER_MINUTE]: 'Mikrobar na minutę',
  [EUnitOfMeasurement.MICRON]: 'Mikron',
  [EUnitOfMeasurement.MICRON_HG_PER_HOUR]: 'Mikron Hg na godzinę',
  [EUnitOfMeasurement.MICRON_HG_PER_MINUTE]: 'Mikron Hg na minutę',
  [EUnitOfMeasurement.MICRON_OF_MERCURY]: 'Mikron słupa rtęci',
  [EUnitOfMeasurement.MILLIBAR]: 'Milibar',
  [EUnitOfMeasurement.MILLIBAR_PER_MINUTE]: 'Milibar na minutę',
  [EUnitOfMeasurement.MILLIGRAM]: 'Miligram',
  [EUnitOfMeasurement.MILLILITRE]: 'Mililitr',
  [EUnitOfMeasurement.MILLILITRE_PER_MINUTE]: 'Mililitr na minutę',
  [EUnitOfMeasurement.MILLIMETRE]: 'Milimetr',
  [EUnitOfMeasurement.MILLIMETRE_OF_MERCURY]: 'Milimetr słupa rtęci',
  [EUnitOfMeasurement.MILLIMETRE_OF_WATER_COLUMN]: 'Milimetr słupa wody',
  [EUnitOfMeasurement.MILLIMETRE_PER_SECOND]: 'Milimetr na sekundę',
  [EUnitOfMeasurement.MILLITORR_PER_HOUR]: 'Millitorr na godzinę',
  [EUnitOfMeasurement.MILLIVOLT]: 'Miliwolt',
  [EUnitOfMeasurement.MILLI_AMPERE]: 'Miliamper',
  [EUnitOfMeasurement.MINUTE]: 'Minuta',
  [EUnitOfMeasurement.MONTH]: 'Miesiąc',
  [EUnitOfMeasurement.NEWTON_PER_SQUARE_MILLIMETRE]: 'Niuton na milimetr kwadratowy',
  [EUnitOfMeasurement.NORMAL_LITRE_PER_SQUARE_METERS_PER_HOUR]: 'Normalny litr na metr kwadratowy na godzinę',
  [EUnitOfMeasurement.NUMBER]: 'Ilość/Numer',
  [EUnitOfMeasurement.PARTS_PER_MILLION]: 'Części na milion',
  [EUnitOfMeasurement.PASCAL]: 'Paskal',
  [EUnitOfMeasurement.PERCENT]: 'Procent',
  [EUnitOfMeasurement.PERCENT_CARBON]: 'Procent węgla',
  [EUnitOfMeasurement.PERCENT_HYDROGEN]: 'Procent wodoru',
  [EUnitOfMeasurement.PERCENT_NITROGEN]: 'Procent azotu',
  [EUnitOfMeasurement.POUND]: 'Funt',
  [EUnitOfMeasurement.PSI]: 'PSI',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS]: 'Twardość Rockwella',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_N]: 'Twardość Rockwella 15N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_T]: 'Twardość Rockwella 15T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_W]: 'Twardość Rockwella 15W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_X]: 'Twardość Rockwella 15X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_Y]: 'Twardość Rockwella 15Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_N]: 'Twardość Rockwella 30N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_T]: 'Twardość Rockwella 30T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_W]: 'Twardość Rockwella 30W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_X]: 'Twardość Rockwella 30X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_Y]: 'Twardość Rockwella 30Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_N]: 'Twardość Rockwella 45N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_T]: 'Twardość Rockwella 45T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_W]: 'Twardość Rockwella 45W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_X]: 'Twardość Rockwella 45X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_Y]: 'Twardość Rockwella 45Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_A]: 'Twardość Rockwella A',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_B]: 'Twardość Rockwella B',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_C]: 'Twardość Rockwella C',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_D]: 'Twardość Rockwella D',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_E]: 'Twardość Rockwella E',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_F]: 'Twardość Rockwella F',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_G]: 'Twardość Rockwella G',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_H]: 'Twardość Rockwella H',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_K]: 'Twardość Rockwella K',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_L]: 'Twardość Rockwella L',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_M]: 'Twardość Rockwella M',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_P]: 'Twardość Rockwella P',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_R]: 'Twardość Rockwella R',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_S]: 'Twardość Rockwella S',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_V]: 'Twardość Rockwella V',
  [EUnitOfMeasurement.RPM]: 'Obroty na minutę (RPM)',
  [EUnitOfMeasurement.SECOND]: 'Sekunda',
  [EUnitOfMeasurement.TONNE]: 'Tona',
  [EUnitOfMeasurement.TORR]: 'Torr',
  [EUnitOfMeasurement.TORR_PER_MINUTE]: 'Torr na minutę',
  [EUnitOfMeasurement.UNIT_LESS]: 'Nieokreślone',
  [EUnitOfMeasurement.THOUSANDTHS_OF_AN_INCH]: 'Tysięczna część cala',
  [EUnitOfMeasurement.THOUSANDTHS_OF_WATER_COLUMN]: 'Tysięczna część cala słupa wody',
  [EUnitOfMeasurement.THOUSANDTHS_OF_MERCURY_COLUMN]: 'Tysięczna część cala słupa rtęci',
  [EUnitOfMeasurement.VICKERS_HARDNESS]: 'Twardość Vickersa',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_1]: 'Twardość Vickersa 0.1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_2]: 'Twardość Vickersa 0.2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_3]: 'Twardość Vickersa 0.3',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_4]: 'Twardość Vickersa 0.4',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_5]: 'Twardość Vickersa 0.5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_6]: 'Twardość Vickersa 0.6',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_7]: 'Twardość Vickersa 0.7',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_8]: 'Twardość Vickersa 0.8',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_9]: 'Twardość Vickersa 0.9',
  [EUnitOfMeasurement.VICKERS_HARDNESS_100]: 'Twardość Vickersa 100',
  [EUnitOfMeasurement.VICKERS_HARDNESS_10]: 'Twardość Vickersa 10',
  [EUnitOfMeasurement.VICKERS_HARDNESS_1]: 'Twardość Vickersa 1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_20]: 'Twardość Vickersa 20',
  [EUnitOfMeasurement.VICKERS_HARDNESS_2]: 'Twardość Vickersa 2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_30]: 'Twardość Vickersa 30',
  [EUnitOfMeasurement.VICKERS_HARDNESS_40]: 'Twardość Vickersa 40',
  [EUnitOfMeasurement.VICKERS_HARDNESS_50]: 'Twardość Vickersa 50',
  [EUnitOfMeasurement.VICKERS_HARDNESS_5]: 'Twardość Vickersa 5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_60]: 'Twardość Vickersa 60',
  [EUnitOfMeasurement.VICKERS_HARDNESS_70]: 'Twardość Vickersa 70',
  [EUnitOfMeasurement.VICKERS_HARDNESS_80]: 'Twardość Vickersa 80',
  [EUnitOfMeasurement.VICKERS_HARDNESS_90]: 'Twardość Vickersa 90',
  [EUnitOfMeasurement.VOLT]: 'Wolt',
  [EUnitOfMeasurement.VOLT_AMPERE]: 'Woltamper',
  [EUnitOfMeasurement.WATT]: 'Wat',
  [EUnitOfMeasurement.WEEK]: 'Tydzień',
  [EUnitOfMeasurement.WEIGHT_PERCENT]: 'Procent masowy',
  [EUnitOfMeasurement.YEAR]: 'Rok',
};

const UnitOfMeasurementSymbol: Record<EUnitOfMeasurement, string> = {
  ...NonTranslatable.UnitOfMeasurementSymbol,
  [EUnitOfMeasurement.MINUTE]: 'Minuta | Minuty | Minut',
  [EUnitOfMeasurement.HOUR]: 'Godzina | Godziny | Godzin',
  [EUnitOfMeasurement.DAY]: 'Dzień | Dni | Dni',
  [EUnitOfMeasurement.WEEK]: 'Tydzień | Tygodnie | Tygodni',
  [EUnitOfMeasurement.MONTH]: 'Miesiąc | Miesiące | Miesięcy',
  [EUnitOfMeasurement.YEAR]: 'Rok | Lata | Lat',
};

const AuditResultType: Record<EAuditResultType, string> = {
  [EAuditResultType.FAILED]: 'Nie zaliczono',
  [EAuditResultType.INCONCLUSIVE]: 'Nierozstrzygnięte',
  [EAuditResultType.PASSED]: 'Zaliczono',
};

const PhysicalPropertyMeaning: Record<EPhysicalPropertyMeaningMap, string> = {
  [EPhysicalPropertyMeaningMap.LOAD_MASS]: 'Masa ładunku',
};

const ConsumptionMeaning: Record<EConsumptionMeaningMap, string> = {
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_CONSUMPTION]: 'Zużycie amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_COST]: 'Koszt amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_CONSUMPTION]: 'Zużycie dwutlenku węgla',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_COST]: 'Koszt dwutlenku węgla',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_CONSUMPTION]: 'Zużycie gazu CO',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_COST]: 'Koszt gazu CO',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_CONSUMPTION]: 'Zużycie zdysocjowanego amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_COST]: 'Koszt zdysocjowanego amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_CONSUMPTION]: 'Zużycie energii elektrycznej',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_COST]: 'Koszt energii elektrycznej',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_CONSUMPTION]: 'Zużycie gazu Endo',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_COST]: 'Koszt gazu Endo',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_CONSUMPTION]: 'Zużycie wodoru',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_COST]: 'Koszt wodoru',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_CONSUMPTION]: 'Zużycie azotu',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_COST]: 'Koszt azotu',
  [EConsumptionMeaningMap.ACETYLENE_C2H2_CONSUMPTION]: 'Zużycie acetylenu (C2H2)',
  [EConsumptionMeaningMap.AIR_CONSUMPTION]: 'Zużycie powietrza',
  [EConsumptionMeaningMap.AMMONIA_NH3_CONSUMPTION]: 'Zużycie amoniaku (NH3)',
  [EConsumptionMeaningMap.ARGON_CONSUMPTION]: 'Zużycie argonu',
  [EConsumptionMeaningMap.CARBON_DIOXIDE_CO2_CONSUMPTION]: 'Zużycie dwutlenku węgla (CO2)',
  [EConsumptionMeaningMap.CARBON_MONOXIDE_CO_CONSUMPTION]: 'Zużycie tlenku węgla (CO)',
  [EConsumptionMeaningMap.DISSOCIATED_AMMONIA_NH3_CONSUMPTION]: 'Zużycie zdysocjowanego amoniaku (NH3)',
  [EConsumptionMeaningMap.ENDO_GAS_NATURAL_CONSUMPTION]: 'Zużycie gazu endo (naturalnego)',
  [EConsumptionMeaningMap.ENDO_GAS_PROPANE_CONSUMPTION]: 'Zużycie gazu Endo (propanu)',
  [EConsumptionMeaningMap.ETHENE_C2H4_CONSUMPTION]: 'Zużycie etenu (C2H4)',
  [EConsumptionMeaningMap.HELIUM_CONSUMPTION]: 'Zużycie helu',
  [EConsumptionMeaningMap.HYDROGEN_H2_CONSUMPTION]: 'Zużycie wodoru (H2)',
  [EConsumptionMeaningMap.METHANOL_CH3OH_CONSUMPTION]: 'Zużycie metanolu (CH3OH)',
  [EConsumptionMeaningMap.NATURAL_GAS_CONSUMPTION]: 'Zużycie gazu ziemnego',
  [EConsumptionMeaningMap.NITROGEN_CONSUMPTION]: 'Zużycie azotu',
  [EConsumptionMeaningMap.NITROUS_OXIDE_N2O_CONSUMPTION]: 'Zużycie podtlenku azotu (N2O)',
  [EConsumptionMeaningMap.PROPANE_C3H8_CONSUMPTION]: 'Zużycie propanu (C3H8)',
  [EConsumptionMeaningMap.WATER_CONSUMPTION]: 'Zużycie wody',
  [EConsumptionMeaningMap.ETHANE_C2H6_CONSUMPTION]: 'Zużycie etanu (C2H6)',
  [EConsumptionMeaningMap.METHANE_CH4_CONSUMPTION]: 'Zużycie metanu (CH4)',
  [EConsumptionMeaningMap.ELECTRICAL_POWER_CONSUMPTION]: 'Pobór mocy elektrycznej',
  [EConsumptionMeaningMap.USER_DEFINED_001]: 'Zużycie zdefiniowane przez użytkownika oznaczające 020',
  [EConsumptionMeaningMap.USER_DEFINED_002]: 'Zużycie zdefiniowane przez użytkownika, znaczenie 019',
  [EConsumptionMeaningMap.USER_DEFINED_003]: 'Zużycie zdefiniowane przez użytkownika oznacza 018',
  [EConsumptionMeaningMap.USER_DEFINED_004]: 'Zużycie zdefiniowane przez użytkownika oznaczające 017',
  [EConsumptionMeaningMap.USER_DEFINED_005]: 'Zużycie zdefiniowane przez użytkownika oznaczające 016',
  [EConsumptionMeaningMap.USER_DEFINED_006]: 'Zużycie zdefiniowane przez użytkownika oznaczające 015',
  [EConsumptionMeaningMap.USER_DEFINED_007]: 'Zużycie zdefiniowane przez użytkownika oznaczające 014',
  [EConsumptionMeaningMap.USER_DEFINED_008]: 'Zużycie zdefiniowane przez użytkownika oznaczające 013',
  [EConsumptionMeaningMap.USER_DEFINED_009]: 'Zużycie zdefiniowane przez użytkownika oznaczające 012',
  [EConsumptionMeaningMap.USER_DEFINED_010]: 'Zużycie zdefiniowane przez użytkownika oznaczające 011',
  [EConsumptionMeaningMap.USER_DEFINED_011]: 'Zużycie zdefiniowane przez użytkownika oznaczające 010',
  [EConsumptionMeaningMap.USER_DEFINED_012]: 'Zużycie zdefiniowane przez użytkownika oznaczające 009',
  [EConsumptionMeaningMap.USER_DEFINED_013]: 'Zużycie zdefiniowane przez użytkownika oznaczające 008',
  [EConsumptionMeaningMap.USER_DEFINED_014]: 'Zużycie zdefiniowane przez użytkownika oznaczające 007',
  [EConsumptionMeaningMap.USER_DEFINED_015]: 'Zużycie zdefiniowane przez użytkownika oznaczające 006',
  [EConsumptionMeaningMap.USER_DEFINED_016]: 'Zużycie zdefiniowane przez użytkownika oznaczające 005',
  [EConsumptionMeaningMap.USER_DEFINED_017]: 'Zużycie zdefiniowane przez użytkownika oznaczające 004',
  [EConsumptionMeaningMap.USER_DEFINED_018]: 'Zużycie zdefiniowane przez użytkownika, znaczenie 003',
  [EConsumptionMeaningMap.USER_DEFINED_019]: 'Zużycie zdefiniowane przez użytkownika oznaczające 002',
  [EConsumptionMeaningMap.USER_DEFINED_020]: 'Zużycie zdefiniowane przez użytkownika oznaczające 001',
};

const JobFeatureMeaning: Record<EJobFeatureMeaningMap, string> = {
  [EJobFeatureMeaningMap.HEAT_LOSS_MIN_POWER]: '(WYCOFANE) Minimalna moc utraty ciepła',
  [EJobFeatureMeaningMap.HEAT_LOSS_MAX_TEMPERATURE]: '(WYCOFANE) Maksymalna temperatura utraty ciepła',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_ACCURACY]: 'Dokładność kontroli temperatury',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_PRECISION]: 'Precyzja kontroli temperatury',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Dokładność regulacji mocy grzewczej',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_PRECISION]: 'Precyzja sterowania mocą grzewczą',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_ACCURACY]: 'Dokładność sterowania mocą elektryczną',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_PRECISION]: 'Precyzja sterowania mocą elektryczną',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Dokładność sterowania mocą wyjściową chłodzenia',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_PRECISION]: 'Precyzja sterowania mocą wyjściową chłodzenia',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem względnym',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem względnym',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_ACCURACY]: 'Dokładność regulacji temperatury w komorze',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_PRECISION]: 'Precyzja kontroli temperatury w komorze',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_ACCURACY]: 'Absolutna dokładność kontroli ciśnienia',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_PRECISION]: 'Absolutna precyzja kontroli ciśnienia',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Dokładność sterowania podciśnieniem',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_PRECISION]: 'Precyzja sterowania podciśnieniem',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem atmosferycznym',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem atmosferycznym',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_ACCURACY]: 'Dokładność regulacji temperatury w pomieszczeniu',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_PRECISION]: 'Precyzja kontroli temperatury w pomieszczeniu',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_ACCURACY]: 'Dokładność sterowania aktywnością aH',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością aH',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania HP',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania HP',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kH',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kH',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_ACCURACY]: 'Dokładność kontroli aktywności aO',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością aO',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania OP',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania OP',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kO',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kO',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_ACCURACY]: 'Dokładność sterowania aktywnością aC',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością aC',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania CP',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania CP',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kC',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kC',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_ACCURACY]: 'Dokładność kontroli aktywności aN',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością aN',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania NP',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania NP',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kN',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kN',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_ACCURACY]: 'Dokładność sterowania aktywnością aB',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością aB',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_ACCURACY]: 'Potencjalna dokładność kontroli ciśnienia tętniczego krwi',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_PRECISION]: 'Potencjalna precyzja kontroli BP',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kB',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kB',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_ACCURACY]: 'Dokładność sterowania aktywnością jako S',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_PRECISION]: 'Precyzja sterowania aktywnością jako S',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania SP',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania SP',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_ACCURACY]: 'Potencjalna dokładność sterowania kS',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_PRECISION]: 'Potencjalna precyzja sterowania kS',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem acetylenu C2H2',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem acetylenu C2H2',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem powietrza',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem powietrza',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem amoniaku NH3',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem amoniaku NH3',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem argonu',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem argonu',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem tlenku węgla CO',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem tlenku węgla CO',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem zdysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem zdysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania naturalnym przepływem gazu Endo',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania naturalnym przepływem gazu Endo',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem gazu Endo propanu',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem gazu propan-butan Endo',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem etenu C2H4',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem Ethene C2H4',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem helu',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem helu',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem wodoru H2',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem wodoru H2',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem metanolu CH3OH',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem metanolu CH3OH',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem gazu ziemnego',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem gazu ziemnego',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem azotu',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem azotu',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem propanu C3H8',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem propanu C3H8',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem wody',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem wody',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem podtlenku azotu N2O',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem podtlenku azotu N2O',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_AGILITY]: 'Elastyczność kontroli temperatury',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_AGILITY]: 'Elastyczność sterowania mocą grzewczą',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_AGILITY]: 'Elastyczność sterowania mocą elektryczną',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_AGILITY]: 'Elastyczność sterowania mocą wyjściową chłodzenia',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem względnym',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_AGILITY]: 'Elastyczność kontroli temperatury w komorze',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_AGILITY]: 'Absolutna zwinność kontroli ciśnienia',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_AGILITY]: 'Elastyczność sterowania podciśnieniem',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem atmosferycznym',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_AGILITY]: 'Elastyczność sterowania temperaturą w pomieszczeniu',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_AGILITY]: 'Aktywność aH kontrola zwinności',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania HP',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kH',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_AGILITY]: 'Aktywność aO kontrola zwinności',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania OP',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kO',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_AGILITY]: 'Aktywność aC kontrola zwinności',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania CP',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kC',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_AGILITY]: 'Aktywność aN kontrola zwinności',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania NP',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kN',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_AGILITY]: 'Aktywność aB kontrola zwinności',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_AGILITY]: 'Potencjalna zwinność kontroli BP',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kB',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_AGILITY]: 'Aktywność jako zwinność sterowania',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania SP',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_AGILITY]: 'Potencjalna zwinność sterowania kS',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem acetylenu C2H2',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem powietrza',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem amoniaku NH3',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem argonu',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_AGILITY]: 'Elastyczność kontroli przepływu tlenku węgla CO',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem zdysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_AGILITY]: 'Zwinność kontroli naturalnego przepływu gazu Endo',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem gazu propan-butan Endo',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem Ethene C2H4',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem helu',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem wodoru H2',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem metanolu CH3OH',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem gazu ziemnego',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem azotu',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem propanu C3H8',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem wody',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem podtlenku azotu N2O',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem tlenu O2',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem tlenu O2',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem tlenu O2',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem metanu CH4',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem metanu CH4',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_AGILITY]: 'Elastyczność sterowania przepływem metanu CH4',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_ACCURACY]: 'Dokładność kontroli dysocjacji',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_PRECISION]: 'Precyzja kontroli dysocjacji',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_AGILITY]: 'Zwinność kontroli dysocjacji',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_ACCURACY]: 'Dokładność regulacji ciśnienia parcjalnego acetylenu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego acetylenu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym acetylenu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_ACCURACY]: 'Dokładność regulacji ciśnienia parcjalnego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_AGILITY]: 'Elastyczność kontroli ciśnienia parcjalnego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_AGILITY]: 'Elastyczność kontroli ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym tlenku węgla CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego tlenku węgla CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_AGILITY]: 'Elastyczność kontroli ciśnienia parcjalnego tlenku węgla CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego dysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego dysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_AGILITY]: 'Elastyczność kontroli ciśnienia parcjalnego zdysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym gazu naturalnego endo',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_PRECISION]: 'Precyzja sterowania naturalnym ciśnieniem parcjalnym gazu endo',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_AGILITY]: 'Zwinność sterowania naturalnego gazem endociśnieniowym przy ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym gazu endopropanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym gazu endopropanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym gazu endopropanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym etenu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym etenu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym etenu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym wodoru H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym wodoru H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym wodoru H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym azotu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym azotu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym azotu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym propanu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym propanu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym propanu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem częściowym wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej ciśnienia parcjalnego acetylenu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego acetylenu C2H2 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentowym ciśnieniem parcjalnym acetylenu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania procentowym ciśnieniem parcjalnym powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_PRECISION]: 'Precyzja sterowania procentowym ciśnieniem parcjalnym powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego powietrza',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego amoniaku NH3 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego amoniaku NH3 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Zdolność kontroli procentowego ciśnienia parcjalnego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowego ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_AGILITY]: 'Elastyczność sterowania procentem ciśnienia parcjalnego argonu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości dwutlenku węgla w ciśnieniu parcjalnym CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej ciśnienia parcjalnego dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego dwutlenku węgla CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości tlenku węgla w ciśnieniu parcjalnym CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego tlenku węgla CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_AGILITY]: 'Zwinność kontroli procentowego ciśnienia parcjalnego tlenku węgla CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości amoniaku NH3 w zdysocjowanym ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej ciśnienia parcjalnego dysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Parametrowa kontrola ciśnienia dysocjowanego amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania procentowym ciśnieniem parcjalnym gazu endo-naturalnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej ciśnienia parcjalnego gazu endo-naturalnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_AGILITY]: 'Parametr ciśnienia gazu endo-naturalnego, procentowa zwinność sterowania',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym gazu endo w procentach propanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym gazu endo w procentach propanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym gazu endo w procentach propanu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej ciśnienia parcjalnego etenu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego etenu C2H4 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_AGILITY]: 'Parametrowa zwinność sterowania ciśnieniem parcjalnym etenu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowego ciśnienia parcjalnego helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego helu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego wodoru H2 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego wodoru H2 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym wodoru H2 procent',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości metanolu CH3OH w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego metanolu CH3OH w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_AGILITY]: 'Elastyczność kontroli procentowej zawartości metanolu CH3OH w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej ciśnienia parcjalnego gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_AGILITY]: 'Elastyczność sterowania procentowym ciśnieniem parcjalnym gazu ziemnego',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości azotu w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego azotu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego azotu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej ciśnienia parcjalnego podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej ciśnienia parcjalnego podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_AGILITY]: 'Parametrowa kontrola ciśnienia podtlenku azotu N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego propanu C3H8 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego propanu C3H8 w procentach',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_AGILITY]: 'Parametrowa zwinność sterowania ciśnieniem parcjalnym propanu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania procentowym ciśnieniem parcjalnym wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_PRECISION]: 'Precyzja sterowania procentowym ciśnieniem parcjalnym wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego wody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem parcjalnym tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego węgla',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego węgla',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem cząstkowym węgla',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym boru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem parcjalnym boru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_AGILITY]: 'Elastyczność sterowania ciśnieniem parcjalnym boru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_ACCURACY]: 'Dokładność kontroli ciśnienia parcjalnego siarki',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego siarki',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_AGILITY]: 'Sprawność kontroli ciśnienia parcjalnego siarki',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania procentowym ciśnieniem parcjalnym tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego ciśnienia parcjalnego tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego tlenu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowego udziału węgla w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowego udziału węgla w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_AGILITY]: 'Elastyczność sterowania procentem węgla w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości boru w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej zawartości boru w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia parcjalnego boru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej zawartości siarki w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej zawartości siarki w ciśnieniu parcjalnym',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia cząstkowego siarki',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_ACCURACY]: 'Dokładność sterowania przepływem etanu C2H6',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_AGILITY]: 'Zwinność sterowania przepływem etanu C2H6',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_PRECISION]: 'Precyzja sterowania przepływem etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem cząstkowym etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_AGILITY]: 'Zwinność sterowania ciśnieniem cząstkowym etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_PRECISION]: 'Precyzja sterowania ciśnieniem cząstkowym etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_ACCURACY]: 'Dokładność sterowania procentem ciśnienia cząstkowego etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_AGILITY]: 'Zwinność sterowania procentem ciśnienia cząstkowego etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_PRECISION]: 'Precyzja sterowania procentem ciśnienia cząstkowego etanu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_ACCURACY]: 'Dokładność sterowania ciśnieniem parcjalnym metanu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_AGILITY]: 'Elastyczność kontroli ciśnienia parcjalnego metanu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_PRECISION]: 'Precyzja kontroli ciśnienia parcjalnego metanu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_ACCURACY]: 'Dokładność kontroli procentowej ciśnienia parcjalnego metanu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_AGILITY]: 'Zwinność kontroli procentowej ciśnienia parcjalnego metanu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_PRECISION]: 'Precyzja kontroli procentowej ciśnienia parcjalnego metanu CH4',
  [EJobFeatureMeaningMap.HEAT_LOSS_AT_500C]: 'Utrata ciepła',
  [EJobFeatureMeaningMap.ASSET_DWELLING_TIME]: 'Asset dwelling time per job',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Wysoka dokładność kontroli ciśnienia próżniowego',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Wysoka sprawność sterowania podciśnieniem',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Wysoka precyzja kontroli ciśnienia próżniowego',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Niska dokładność sterowania podciśnieniem',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Elastyczność sterowania niskim ciśnieniem podciśnienia',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Precyzja sterowania niskim ciśnieniem próżniowym',
  [EJobFeatureMeaningMap.VACUUM_TIME_TO_CROSS_OVER]: 'Czas na przejście próżni',
  [EJobFeatureMeaningMap.VACUUM_MAX_LEAK_UP_RATE]: 'Maksymalny czas wycieku',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_ACCURACY]: 'Dokładność kontroli czasu realizacji',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_AGILITY]: 'Elastyczność kontroli czasu realizacji',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_PRECISION]: 'Precyzja kontroli czasu realizacji',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_ACCURACY]: 'Dokładność sterowania prędkością taśmy',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_AGILITY]: 'Zwinność sterowania prędkością taśmy',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_PRECISION]: 'Precyzja sterowania prędkością taśmy',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_ACCURACY]: 'Dokładność kontroli temperatury ładunku',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_AGILITY]: 'Elastyczność kontroli temperatury ładunku',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_PRECISION]: 'Precyzja kontroli temperatury obciążenia',
  [EJobFeatureMeaningMap.PEARSON_CORRELATION]: 'Korelacja Pearsona',
  [EJobFeatureMeaningMap.SPEARMAN_CORRELATION]: 'Korelacja Spearmana',
  [EJobFeatureMeaningMap.USER_DEFINED_001]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 001',
  [EJobFeatureMeaningMap.USER_DEFINED_002]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 002',
  [EJobFeatureMeaningMap.USER_DEFINED_003]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 003',
  [EJobFeatureMeaningMap.USER_DEFINED_004]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 004',
  [EJobFeatureMeaningMap.USER_DEFINED_005]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 005',
  [EJobFeatureMeaningMap.USER_DEFINED_006]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 006',
  [EJobFeatureMeaningMap.USER_DEFINED_007]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 007',
  [EJobFeatureMeaningMap.USER_DEFINED_008]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 008',
  [EJobFeatureMeaningMap.USER_DEFINED_009]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 009',
  [EJobFeatureMeaningMap.USER_DEFINED_010]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 010',
  [EJobFeatureMeaningMap.USER_DEFINED_011]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 011',
  [EJobFeatureMeaningMap.USER_DEFINED_012]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 012',
  [EJobFeatureMeaningMap.USER_DEFINED_013]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 013',
  [EJobFeatureMeaningMap.USER_DEFINED_014]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 014',
  [EJobFeatureMeaningMap.USER_DEFINED_015]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 015',
  [EJobFeatureMeaningMap.USER_DEFINED_016]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 016',
  [EJobFeatureMeaningMap.USER_DEFINED_017]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 017',
  [EJobFeatureMeaningMap.USER_DEFINED_018]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 018',
  [EJobFeatureMeaningMap.USER_DEFINED_019]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 019',
  [EJobFeatureMeaningMap.USER_DEFINED_020]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 020',
  [EJobFeatureMeaningMap.USER_DEFINED_021]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 021',
  [EJobFeatureMeaningMap.USER_DEFINED_022]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 022',
  [EJobFeatureMeaningMap.USER_DEFINED_023]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 023',
  [EJobFeatureMeaningMap.USER_DEFINED_024]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 024',
  [EJobFeatureMeaningMap.USER_DEFINED_025]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 025',
  [EJobFeatureMeaningMap.USER_DEFINED_026]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 026',
  [EJobFeatureMeaningMap.USER_DEFINED_027]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 027',
  [EJobFeatureMeaningMap.USER_DEFINED_028]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 028',
  [EJobFeatureMeaningMap.USER_DEFINED_029]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 029',
  [EJobFeatureMeaningMap.USER_DEFINED_030]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 030',
  [EJobFeatureMeaningMap.USER_DEFINED_031]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 031',
  [EJobFeatureMeaningMap.USER_DEFINED_032]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 032',
  [EJobFeatureMeaningMap.USER_DEFINED_033]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 033',
  [EJobFeatureMeaningMap.USER_DEFINED_034]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 034',
  [EJobFeatureMeaningMap.USER_DEFINED_035]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 035',
  [EJobFeatureMeaningMap.USER_DEFINED_036]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 036',
  [EJobFeatureMeaningMap.USER_DEFINED_037]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 037',
  [EJobFeatureMeaningMap.USER_DEFINED_038]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 038',
  [EJobFeatureMeaningMap.USER_DEFINED_039]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 039',
  [EJobFeatureMeaningMap.USER_DEFINED_040]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 040',
  [EJobFeatureMeaningMap.USER_DEFINED_041]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 041',
  [EJobFeatureMeaningMap.USER_DEFINED_042]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 042',
  [EJobFeatureMeaningMap.USER_DEFINED_043]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 043',
  [EJobFeatureMeaningMap.USER_DEFINED_044]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 044',
  [EJobFeatureMeaningMap.USER_DEFINED_045]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 045',
  [EJobFeatureMeaningMap.USER_DEFINED_046]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 046',
  [EJobFeatureMeaningMap.USER_DEFINED_047]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 047',
  [EJobFeatureMeaningMap.USER_DEFINED_048]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 048',
  [EJobFeatureMeaningMap.USER_DEFINED_049]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 049',
  [EJobFeatureMeaningMap.USER_DEFINED_050]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 050',
  [EJobFeatureMeaningMap.USER_DEFINED_051]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 051',
  [EJobFeatureMeaningMap.USER_DEFINED_052]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 052',
  [EJobFeatureMeaningMap.USER_DEFINED_053]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 053',
  [EJobFeatureMeaningMap.USER_DEFINED_054]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 054',
  [EJobFeatureMeaningMap.USER_DEFINED_055]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 055',
  [EJobFeatureMeaningMap.USER_DEFINED_056]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 056',
  [EJobFeatureMeaningMap.USER_DEFINED_057]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 057',
  [EJobFeatureMeaningMap.USER_DEFINED_058]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 058',
  [EJobFeatureMeaningMap.USER_DEFINED_059]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 059',
  [EJobFeatureMeaningMap.USER_DEFINED_060]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 060',
  [EJobFeatureMeaningMap.USER_DEFINED_061]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 061',
  [EJobFeatureMeaningMap.USER_DEFINED_062]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 062',
  [EJobFeatureMeaningMap.USER_DEFINED_063]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 063',
  [EJobFeatureMeaningMap.USER_DEFINED_064]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 064',
  [EJobFeatureMeaningMap.USER_DEFINED_065]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 065',
  [EJobFeatureMeaningMap.USER_DEFINED_066]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 066',
  [EJobFeatureMeaningMap.USER_DEFINED_067]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 067',
  [EJobFeatureMeaningMap.USER_DEFINED_068]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 068',
  [EJobFeatureMeaningMap.USER_DEFINED_069]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 069',
  [EJobFeatureMeaningMap.USER_DEFINED_070]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 070',
  [EJobFeatureMeaningMap.USER_DEFINED_071]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 071',
  [EJobFeatureMeaningMap.USER_DEFINED_072]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 072',
  [EJobFeatureMeaningMap.USER_DEFINED_073]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 073',
  [EJobFeatureMeaningMap.USER_DEFINED_074]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 074',
  [EJobFeatureMeaningMap.USER_DEFINED_075]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 075',
  [EJobFeatureMeaningMap.USER_DEFINED_076]: 'Zdefiniowana przez użytkownika funkcja zadania o znaczeniu 076',
  [EJobFeatureMeaningMap.USER_DEFINED_077]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 077',
  [EJobFeatureMeaningMap.USER_DEFINED_078]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 078',
  [EJobFeatureMeaningMap.USER_DEFINED_079]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 079',
  [EJobFeatureMeaningMap.USER_DEFINED_080]: 'Zdefiniowana przez użytkownika funkcja zadania oznaczająca 080',
};

const TimeseriesMeaning: Record<ETimeseriesMeaning, string> = {
  [ETimeseriesMeaning.TEMPERATURE_SETPOINT]: 'Nastawa temperatury',
  [ETimeseriesMeaning.TEMPERATURE_READING]: 'Odczyt temperatury',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_SETPOINT]: 'Zadana wartość mocy grzewczej',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_READING]: 'Odczyt mocy grzewczej',
  [ETimeseriesMeaning.HEATING_POWER_ENABLED]: 'Moc grzania włączona',
  [ETimeseriesMeaning.ELECTRICAL_POWER_SETPOINT]: 'Punkt zadany mocy elektrycznej',
  [ETimeseriesMeaning.ELECTRICAL_POWER_READING]: 'Odczyt mocy elektrycznej',
  [ETimeseriesMeaning.TOTAL_HEATING_GAS_SUPPLY_FLOW_READING]: 'Odczyt całkowitego przepływu gazu grzewczego',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_SETPOINT]: 'Zadana moc wyjściowa chłodzenia',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_READING]: 'Odczyt mocy wyjściowej chłodzenia',
  [ETimeseriesMeaning.COOLING_POWER_ENABLED]: 'Włączono moc chłodzenia',
  [ETimeseriesMeaning.TOTAL_COOLING_GAS_SUPPLY_FLOW_READING]: 'Odczyt całkowitego przepływu gazu chłodzącego',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_SETPOINT]: 'Wartość zadana ciśnienia względnego',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_READING]: 'Odczyt ciśnienia względnego',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_SETPOINT]: 'Zadana temperatura komory',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_READING]: 'Odczyt temperatury w komorze',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_SETPOINT]: 'Wartość zadana ciśnienia bezwzględnego',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_READING]: 'Odczyt ciśnienia bezwzględnego',
  [ETimeseriesMeaning.VACUUM_PRESSURE_SETPOINT]: 'Wartość zadana ciśnienia próżniowego',
  [ETimeseriesMeaning.VACUUM_PRESSURE_READING]: 'Odczyt ciśnienia próżni',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_SETPOINT]: 'Punkt nastawy ciśnienia atmosferycznego',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_READING]: 'Odczyt ciśnienia atmosferycznego',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_SETPOINT]: 'Zadana temperatura w pomieszczeniu',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_READING]: 'Odczyt temperatury pokojowej',
  [ETimeseriesMeaning.ACTIVITY_AH_SETPOINT]: 'Punkt zadany aktywności aH',
  [ETimeseriesMeaning.ACTIVITY_AH_CALCULATION]: 'Obliczanie aktywności aH',
  [ETimeseriesMeaning.POTENTIAL_HP_SETPOINT]: 'Potencjalna wartość zadana HP',
  [ETimeseriesMeaning.POTENTIAL_HP_CALCULATION]: 'Obliczanie potencjalnej mocy',
  [ETimeseriesMeaning.POTENTIAL_KH_SETPOINT]: 'Potencjalna wartość zadana kH',
  [ETimeseriesMeaning.POTENTIAL_KH_CALCULATION]: 'Obliczanie potencjalnego kH',
  [ETimeseriesMeaning.ACTIVITY_AO_SETPOINT]: 'Punkt zadany aktywności aO',
  [ETimeseriesMeaning.ACTIVITY_AO_CALCULATION]: 'Obliczanie aktywności aO',
  [ETimeseriesMeaning.POTENTIAL_OP_SETPOINT]: 'Potencjalny punkt nastawczy OP',
  [ETimeseriesMeaning.POTENTIAL_OP_CALCULATION]: 'Obliczanie potencjalnego OP',
  [ETimeseriesMeaning.POTENTIAL_KO_SETPOINT]: 'Potencjalna wartość zadana kO',
  [ETimeseriesMeaning.POTENTIAL_KO_CALCULATION]: 'Obliczanie potencjalnego kO',
  [ETimeseriesMeaning.ACTIVITY_AC_SETPOINT]: 'Punkt zadany aktywności aC',
  [ETimeseriesMeaning.ACTIVITY_AC_CALCULATION]: 'Obliczanie aktywności aC',
  [ETimeseriesMeaning.POTENTIAL_CP_SETPOINT]: 'Potencjalny punkt nastawy CP',
  [ETimeseriesMeaning.POTENTIAL_CP_CALCULATION]: 'Obliczanie potencjalnego CP',
  [ETimeseriesMeaning.POTENTIAL_KC_SETPOINT]: 'Potencjalna wartość zadana kC',
  [ETimeseriesMeaning.POTENTIAL_KC_CALCULATION]: 'Obliczanie potencjalnego kC',
  [ETimeseriesMeaning.ACTIVITY_AN_SETPOINT]: 'Punkt nastawy aktywności aN',
  [ETimeseriesMeaning.ACTIVITY_AN_CALCULATION]: 'Obliczanie aktywności aN',
  [ETimeseriesMeaning.POTENTIAL_NP_SETPOINT]: 'Potencjalny punkt nastawy NP',
  [ETimeseriesMeaning.POTENTIAL_NP_CALCULATION]: 'Obliczanie potencjalnego NP',
  [ETimeseriesMeaning.POTENTIAL_KN_SETPOINT]: 'Potencjalna wartość zadana kN',
  [ETimeseriesMeaning.POTENTIAL_KN_CALCULATION]: 'Obliczanie potencjału kN',
  [ETimeseriesMeaning.ACTIVITY_AB_SETPOINT]: 'Punkt zadany aktywności aB',
  [ETimeseriesMeaning.ACTIVITY_AB_CALCULATION]: 'Obliczanie aktywności aB',
  [ETimeseriesMeaning.POTENTIAL_BP_SETPOINT]: 'Potencjalna wartość zadana ciśnienia tętniczego krwi',
  [ETimeseriesMeaning.POTENTIAL_BP_CALCULATION]: 'Obliczanie potencjalnego ciśnienia tętniczego',
  [ETimeseriesMeaning.POTENTIAL_KB_SETPOINT]: 'Potencjalna wartość zadana kB',
  [ETimeseriesMeaning.POTENTIAL_KB_CALCULATION]: 'Obliczanie potencjalnego kB',
  [ETimeseriesMeaning.ACTIVITY_AS_SETPOINT]: 'Punkt zadany aktywności jako S',
  [ETimeseriesMeaning.ACTIVITY_AS_CALCULATION]: 'Obliczanie aktywności jako S',
  [ETimeseriesMeaning.POTENTIAL_SP_SETPOINT]: 'Potencjalna wartość zadana SP',
  [ETimeseriesMeaning.POTENTIAL_SP_CALCULATION]: 'Obliczanie potencjalnego SP',
  [ETimeseriesMeaning.POTENTIAL_KS_SETPOINT]: 'Potencjalna wartość zadana kS',
  [ETimeseriesMeaning.POTENTIAL_KS_CALCULATION]: 'Obliczanie potencjalnego kS',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Punkt nastawy przepływu acetylenu C2H2',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_READING]: 'Odczyt przepływu acetylenu C2H2',
  [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Punkt zadany przepływu powietrza',
  [ETimeseriesMeaning.AIR_FLOW_READING]: 'Odczyt przepływu powietrza',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Punkt nastawy przepływu amoniaku NH3',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_READING]: 'Odczyt przepływu amoniaku NH3',
  [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Punkt nastawy przepływu argonu',
  [ETimeseriesMeaning.ARGON_FLOW_READING]: 'Odczyt przepływu argonu',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'Punkt zadany przepływu dwutlenku węgla CO2',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_READING]: 'Odczyt przepływu dwutlenku węgla CO2',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'Punkt zadany przepływu tlenku węgla CO',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_READING]: 'Odczyt przepływu tlenku węgla CO',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Punkt nastawy przepływu zdysocjowanego amoniaku NH3',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_READING]: 'Odczyt przepływu zdysocjowanego amoniaku NH3',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Punkt zadany naturalnego przepływu gazu Endo',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_READING]: 'Odczyt naturalnego przepływu gazu Endo',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Punkt nastawy przepływu gazu Endo propanu',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_READING]: 'Odczyt przepływu propanu w gazie Endo',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Punkt nastawy przepływu etenu C2H4',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_READING]: 'Odczyt przepływu etenu C2H4',
  [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Punkt nastawy przepływu helu',
  [ETimeseriesMeaning.HELIUM_FLOW_READING]: 'Odczyt przepływu helu',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Punkt zadany przepływu wodoru H2',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_READING]: 'Odczyt przepływu wodoru H2',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Punkt nastawy przepływu metanolu CH3OH',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_READING]: 'Odczyt przepływu metanolu CH3OH',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Wartość zadana przepływu gazu ziemnego',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_READING]: 'Odczyt przepływu gazu ziemnego',
  [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Punkt zadany przepływu azotu',
  [ETimeseriesMeaning.NITROGEN_FLOW_READING]: 'Odczyt przepływu azotu',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Punkt nastawy przepływu propanu C3H8',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_READING]: 'Odczyt przepływu propanu C3H8',
  [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Punkt zadany przepływu wody',
  [ETimeseriesMeaning.WATER_FLOW_READING]: 'Odczyt przepływu wody',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'Punkt nastawy przepływu podtlenku azotu N2O',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_READING]: 'Odczyt przepływu podtlenku azotu N2O',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Punkt nastawy przepływu metanu CH4',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_READING]: 'Odczyt przepływu metanu CH4',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Punkt nastawy przepływu tlenu O2',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_READING]: 'Odczyt przepływu tlenu O2',
  [ETimeseriesMeaning.DISSOCIATION_SETPOINT]: 'Punkt nastawczy dysocjacji',
  [ETimeseriesMeaning.DISSOCIATION_READING]: 'Odczyt dysocjacyjny',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_SETPOINT]: 'Wartość zadana ciśnienia parcjalnego acetylenu C2H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_READING]: 'Odczyt ciśnienia parcjalnego acetylenu C2H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_SETPOINT]: 'Punkt zadany ciśnienia parcjalnego powietrza',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_READING]: 'Odczyt ciśnienia parcjalnego powietrza',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_READING]: 'Odczyt ciśnienia parcjalnego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego argonu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_READING]: 'Odczyt ciśnienia parcjalnego argonu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_SETPOINT]: 'Punkt nastawczy ciśnienia parcjalnego dwutlenku węgla CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_READING]: 'Odczyt ciśnienia parcjalnego dwutlenku węgla CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_SETPOINT]: 'Punkt nastawczy ciśnienia parcjalnego tlenku węgla CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_READING]: 'Odczyt ciśnienia parcjalnego tlenku węgla CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_SETPOINT]: 'Punkt nastawczy ciśnienia parcjalnego zdysocjowanego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_READING]: 'Odczyt ciśnienia parcjalnego zdysocjowanego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_SETPOINT]: 'Punkt nastawy naturalnej ciśnienia parcjalnego gazu endo',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_READING]: 'Odczyt ciśnienia parcjalnego gazu endo w warunkach naturalnych',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego gazu endopropanowego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_READING]: 'Odczyt ciśnienia parcjalnego gazu endopropanowego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego etenu C2H4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_READING]: 'Odczyt ciśnienia parcjalnego etenu C2H4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego helu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_READING]: 'Odczyt ciśnienia parcjalnego helu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_SETPOINT]: 'Punkt nastawczy ciśnienia parcjalnego wodoru H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_READING]: 'Odczyt ciśnienia parcjalnego wodoru H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_READING]: 'Odczyt ciśnienia parcjalnego metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_SETPOINT]: 'Wartość zadana ciśnienia parcjalnego gazu ziemnego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_READING]: 'Odczyt ciśnienia parcjalnego gazu ziemnego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego azotu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_READING]: 'Odczyt ciśnienia parcjalnego azotu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_SETPOINT]: 'Punkt nastawczy ciśnienia parcjalnego podtlenku azotu N2O',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_READING]: 'Odczyt ciśnienia parcjalnego podtlenku azotu N2O',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego propanu C3H8',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_READING]: 'Odczyt ciśnienia parcjalnego propanu C3H8',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_SETPOINT]: 'Punkt nastawy ciśnienia częściowego wody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_READING]: 'Odczyt ciśnienia parcjalnego wody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego acetylenu C2H2 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego acetylenu C2H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_SETPOINT]: 'Punkt nastawy procentowej ciśnienia parcjalnego powietrza',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego powietrza',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego amoniaku NH3 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_SETPOINT]: 'Punkt odniesienia procentowego ciśnienia parcjalnego argonu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego argonu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_SETPOINT]: 'Punkt nastawczy procentowego ciśnienia parcjalnego dwutlenku węgla CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego dwutlenku węgla CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_SETPOINT]: 'Punkt nastawy procentowej ciśnienia parcjalnego tlenku węgla CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego tlenku węgla CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego zdysocjowanego amoniaku NH3 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego zdysocjowanego amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego gazu endo naturalny w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego gazu endo',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego gazu endo w procentach propanu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego propanu w gazie endo',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego etenu C2H4 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego etenu C2H4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_SETPOINT]: 'Punkt nastawy procentowego ciśnienia parcjalnego helu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_READING]: 'Odczyt procentowego ciśnienia parcjalnego helu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego wodoru H2 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego wodoru H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_SETPOINT]: 'Punkt nastawczy procentowego ciśnienia parcjalnego metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_SETPOINT]: 'Punkt odniesienia procentowego ciśnienia parcjalnego gazu ziemnego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego gazu ziemnego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_SETPOINT]: 'Punkt odniesienia procentowego ciśnienia parcjalnego azotu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego azotu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego podtlenku azotu N2O w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego podtlenku azotu N2O',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego propanu C3H8 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego propanu C3H8',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_SETPOINT]: 'Punkt nastawy procentowej ciśnienia parcjalnego wody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego wody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego tlenu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_READING]: 'Odczyt ciśnienia parcjalnego tlenu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego węgla',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_READING]: 'Odczyt ciśnienia parcjalnego węgla',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego boru',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_READING]: 'Odczyt ciśnienia parcjalnego boru',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego siarki',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_READING]: 'Odczyt ciśnienia parcjalnego siarki',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_SETPOINT]: 'Punkt nastawy procentowego ciśnienia parcjalnego tlenu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_READING]: 'Odczyt procentowego stężenia tlenu w ciśnieniu parcjalnym',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_SETPOINT]: 'Punkt nastawy procentowego ciśnienia parcjalnego węgla',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_READING]: 'Odczyt procentowego stężenia węgla w ciśnieniu parcjalnym',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_SETPOINT]: 'Punkt odniesienia dla procentowego ciśnienia boru parcjalnego',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_READING]: 'Odczyt procentowego ciśnienia parcjalnego boru',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_SETPOINT]: 'Punkt nastawy procentowej zawartości siarki w ciśnieniu parcjalnym',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_READING]: 'Odczyt procentowej zawartości siarki w ciśnieniu parcjalnym',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_SETPOINT]: 'Wartość zadana przepływu etanu C2H6',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_READING]: 'Odczyt przepływu etanu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego etanu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_READING]: 'Odczyt ciśnienia parcjalnego etanu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego etanu C2H6 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego etanu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_SETPOINT]: 'Punkt nastawy ciśnienia parcjalnego metanu CH4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_READING]: 'Odczyt ciśnienia parcjalnego metanu CH4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_SETPOINT]: 'Punkt odniesienia ciśnienia parcjalnego metanu CH4 w procentach',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_READING]: 'Odczyt procentowy ciśnienia parcjalnego metanu CH4',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_SETPOINT]: 'Punkt nastawy wysokiego ciśnienia próżniowego',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_READING]: 'Odczyt wysokiego ciśnienia próżniowego',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_SETPOINT]: 'Niska wartość zadana ciśnienia próżniowego',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_READING]: 'Niski odczyt ciśnienia próżniowego',
  [ETimeseriesMeaning.LEAD_TIME_SETPOINT]: 'Punkt nastawy czasu realizacji',
  [ETimeseriesMeaning.LEAD_TIME_READING]: 'Odczyt w czasie realizacji',
  [ETimeseriesMeaning.BELT_SPEED_SETPOINT]: 'Punkt zadany prędkości taśmy',
  [ETimeseriesMeaning.BELT_SPEED_READING]: 'Odczyt prędkości taśmy',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_SETPOINT]: 'Zadana temperatura obciążenia',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_READING]: 'Odczyt temperatury obciążenia',
  [ETimeseriesMeaning.USER_DEFINED_001]: 'Zdefiniowany przez użytkownika szereg czasowy o znaczeniu 001',
  [ETimeseriesMeaning.USER_DEFINED_002]: 'Szereg czasowy zdefiniowany przez użytkownika o znaczeniu 002',
  [ETimeseriesMeaning.USER_DEFINED_003]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 003',
  [ETimeseriesMeaning.USER_DEFINED_004]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 004',
  [ETimeseriesMeaning.USER_DEFINED_005]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 005',
  [ETimeseriesMeaning.USER_DEFINED_006]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 006',
  [ETimeseriesMeaning.USER_DEFINED_007]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 007',
  [ETimeseriesMeaning.USER_DEFINED_008]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 008',
  [ETimeseriesMeaning.USER_DEFINED_009]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 009',
  [ETimeseriesMeaning.USER_DEFINED_010]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 010',
  [ETimeseriesMeaning.USER_DEFINED_011]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 011',
  [ETimeseriesMeaning.USER_DEFINED_012]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 012',
  [ETimeseriesMeaning.USER_DEFINED_013]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 013',
  [ETimeseriesMeaning.USER_DEFINED_014]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 014',
  [ETimeseriesMeaning.USER_DEFINED_015]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 015',
  [ETimeseriesMeaning.USER_DEFINED_016]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 016',
  [ETimeseriesMeaning.USER_DEFINED_017]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 017',
  [ETimeseriesMeaning.USER_DEFINED_018]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 018',
  [ETimeseriesMeaning.USER_DEFINED_019]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 019',
  [ETimeseriesMeaning.USER_DEFINED_020]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 020',
  [ETimeseriesMeaning.USER_DEFINED_021]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 021',
  [ETimeseriesMeaning.USER_DEFINED_022]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 022',
  [ETimeseriesMeaning.USER_DEFINED_023]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 023',
  [ETimeseriesMeaning.USER_DEFINED_024]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 024',
  [ETimeseriesMeaning.USER_DEFINED_025]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 025',
  [ETimeseriesMeaning.USER_DEFINED_026]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 026',
  [ETimeseriesMeaning.USER_DEFINED_027]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 027',
  [ETimeseriesMeaning.USER_DEFINED_028]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 028',
  [ETimeseriesMeaning.USER_DEFINED_029]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 029',
  [ETimeseriesMeaning.USER_DEFINED_030]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 030',
  [ETimeseriesMeaning.USER_DEFINED_031]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 031',
  [ETimeseriesMeaning.USER_DEFINED_032]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 032',
  [ETimeseriesMeaning.USER_DEFINED_033]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 033',
  [ETimeseriesMeaning.USER_DEFINED_034]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 034',
  [ETimeseriesMeaning.USER_DEFINED_035]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 035',
  [ETimeseriesMeaning.USER_DEFINED_036]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 036',
  [ETimeseriesMeaning.USER_DEFINED_037]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 037',
  [ETimeseriesMeaning.USER_DEFINED_038]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 038',
  [ETimeseriesMeaning.USER_DEFINED_039]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 039',
  [ETimeseriesMeaning.USER_DEFINED_040]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 040',
  [ETimeseriesMeaning.USER_DEFINED_041]: 'Szereg czasowy zdefiniowany przez użytkownika o znaczeniu 041',
  [ETimeseriesMeaning.USER_DEFINED_042]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 042',
  [ETimeseriesMeaning.USER_DEFINED_043]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 043',
  [ETimeseriesMeaning.USER_DEFINED_044]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 044',
  [ETimeseriesMeaning.USER_DEFINED_045]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 045',
  [ETimeseriesMeaning.USER_DEFINED_046]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 046',
  [ETimeseriesMeaning.USER_DEFINED_047]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 047',
  [ETimeseriesMeaning.USER_DEFINED_048]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 048',
  [ETimeseriesMeaning.USER_DEFINED_049]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 049',
  [ETimeseriesMeaning.USER_DEFINED_050]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 050',
  [ETimeseriesMeaning.USER_DEFINED_051]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 051',
  [ETimeseriesMeaning.USER_DEFINED_052]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 052',
  [ETimeseriesMeaning.USER_DEFINED_053]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 053',
  [ETimeseriesMeaning.USER_DEFINED_054]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 054',
  [ETimeseriesMeaning.USER_DEFINED_055]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 055',
  [ETimeseriesMeaning.USER_DEFINED_056]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 056',
  [ETimeseriesMeaning.USER_DEFINED_057]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 057',
  [ETimeseriesMeaning.USER_DEFINED_058]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 058',
  [ETimeseriesMeaning.USER_DEFINED_059]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 059',
  [ETimeseriesMeaning.USER_DEFINED_060]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 060',
  [ETimeseriesMeaning.USER_DEFINED_061]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 061',
  [ETimeseriesMeaning.USER_DEFINED_062]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 062',
  [ETimeseriesMeaning.USER_DEFINED_063]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 063',
  [ETimeseriesMeaning.USER_DEFINED_064]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 064',
  [ETimeseriesMeaning.USER_DEFINED_065]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 065',
  [ETimeseriesMeaning.USER_DEFINED_066]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 066',
  [ETimeseriesMeaning.USER_DEFINED_067]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 067',
  [ETimeseriesMeaning.USER_DEFINED_068]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 068',
  [ETimeseriesMeaning.USER_DEFINED_069]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 069',
  [ETimeseriesMeaning.USER_DEFINED_070]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 070',
  [ETimeseriesMeaning.USER_DEFINED_071]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 071',
  [ETimeseriesMeaning.USER_DEFINED_072]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 072',
  [ETimeseriesMeaning.USER_DEFINED_073]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 073',
  [ETimeseriesMeaning.USER_DEFINED_074]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 074',
  [ETimeseriesMeaning.USER_DEFINED_075]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 075',
  [ETimeseriesMeaning.USER_DEFINED_076]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 076',
  [ETimeseriesMeaning.USER_DEFINED_077]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 077',
  [ETimeseriesMeaning.USER_DEFINED_078]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 078',
  [ETimeseriesMeaning.USER_DEFINED_079]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 079',
  [ETimeseriesMeaning.USER_DEFINED_080]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 080',
  [ETimeseriesMeaning.USER_DEFINED_081]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 081',
  [ETimeseriesMeaning.USER_DEFINED_082]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 082',
  [ETimeseriesMeaning.USER_DEFINED_083]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 083',
  [ETimeseriesMeaning.USER_DEFINED_084]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 084',
  [ETimeseriesMeaning.USER_DEFINED_085]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 085',
  [ETimeseriesMeaning.USER_DEFINED_086]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 086',
  [ETimeseriesMeaning.USER_DEFINED_087]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 087',
  [ETimeseriesMeaning.USER_DEFINED_088]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 088',
  [ETimeseriesMeaning.USER_DEFINED_089]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 089',
  [ETimeseriesMeaning.USER_DEFINED_090]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 090',
  [ETimeseriesMeaning.USER_DEFINED_091]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 091',
  [ETimeseriesMeaning.USER_DEFINED_092]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 092',
  [ETimeseriesMeaning.USER_DEFINED_093]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 093',
  [ETimeseriesMeaning.USER_DEFINED_094]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 094',
  [ETimeseriesMeaning.USER_DEFINED_095]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 095',
  [ETimeseriesMeaning.USER_DEFINED_096]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 096',
  [ETimeseriesMeaning.USER_DEFINED_097]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 097',
  [ETimeseriesMeaning.USER_DEFINED_098]: 'Zdefiniowany przez użytkownika szereg czasowy oznaczający 098',
  [ETimeseriesMeaning.USER_DEFINED_099]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 099',
  [ETimeseriesMeaning.USER_DEFINED_100]: 'Szereg czasowy zdefiniowany przez użytkownika oznaczający 100',
};

const PhysicalDimension: Record<EPhysicalDimension, string> = {
  [EPhysicalDimension.ACCELERATION]: 'Przyspieszenie',
  [EPhysicalDimension.ADMITTANCE]: 'Admitancja',
  [EPhysicalDimension.AMOUNT]: 'Liczność',
  [EPhysicalDimension.ANGLE]: 'Kąt',
  [EPhysicalDimension.ANGULAR_ACCELERATION]: 'Przyspieszenie kątowe',
  [EPhysicalDimension.ANGULAR_MOMENTUM]: 'Moment pędu',
  [EPhysicalDimension.ANGULAR_VELOCITY]: 'Prędkość kątowa',
  [EPhysicalDimension.APPARENT_ELECTRICAL_POWER]: 'Moc pozorna',
  [EPhysicalDimension.AREA]: 'Powierzchnia',
  [EPhysicalDimension.CAPACITANCE]: 'Pojemność elektryczna',
  [EPhysicalDimension.COOLING_RATE]: 'Szybkość chłodzenia',
  [EPhysicalDimension.CURRENCY]: 'Waluta',
  [EPhysicalDimension.DATETIME]: 'Data i godzina',
  [EPhysicalDimension.DATE]: 'Data',
  [EPhysicalDimension.DENSITY]: 'Gęstość',
  [EPhysicalDimension.DURATION]: 'Czas trwania',
  [EPhysicalDimension.ELECTRICAL_REACTIVE_POWER]: 'Moc bierna',
  [EPhysicalDimension.ELECTRICAL_RESISTANCE]: 'Opór elektryczny',
  [EPhysicalDimension.ELECTRIC_CHARGE]: 'Ładunek elektryczny',
  [EPhysicalDimension.ELECTRIC_CURRENT]: 'Prąd elektryczny',
  [EPhysicalDimension.ENERGY]: 'Energia',
  [EPhysicalDimension.FLOW]: 'Przepływ',
  [EPhysicalDimension.FLOW_PER_AREA]: 'Przepływ na powierzchnię',
  [EPhysicalDimension.FORCE]: 'Siła',
  [EPhysicalDimension.FREQUENCY]: 'Częstotliwość',
  [EPhysicalDimension.HARDNESS]: 'Twardość',
  [EPhysicalDimension.HEAT_FLUX]: 'Strumień ciepła',
  [EPhysicalDimension.IDENTITY]: 'identyfikator',
  [EPhysicalDimension.INDUCTIVITY]: 'Indukcyjność',
  [EPhysicalDimension.INTERVAL]: 'Przedział',
  [EPhysicalDimension.LENGTH]: 'Długość',
  [EPhysicalDimension.MAGNETIC_FLUX]: 'Strumień magnetyczny',
  [EPhysicalDimension.MASS]: 'Masa',
  [EPhysicalDimension.MASS_FLOW_RATE]: 'Natężenie przepływu masy',
  [EPhysicalDimension.MASS_FRACTION]: 'Ułamek masowy',
  [EPhysicalDimension.MODULUS_OF_ELASTICITY]: 'Moduł sprężystości',
  [EPhysicalDimension.MOMENTUM]: 'Pęd',
  [EPhysicalDimension.MOMENT_OF_INERTIA]: 'Moment bezwładności',
  [EPhysicalDimension.POWER]: 'Moc',
  [EPhysicalDimension.POWER_DENSITY]: 'Gęstość mocy',
  [EPhysicalDimension.PRESSURE]: 'Ciśnienie',
  [EPhysicalDimension.PRESSURE_RATE]: 'Zmiana ciśnienia w czasie',
  [EPhysicalDimension.QUANTITY]: 'Ilość',
  [EPhysicalDimension.RATIO]: 'Współczynnik',
  [EPhysicalDimension.ROTATIONAL_VELOCITY]: 'Prędkość obrotowa',
  [EPhysicalDimension.SOUND_PRESSURE]: 'Ciśnienie akustyczne',
  [EPhysicalDimension.STRESS]: 'Naprężenie',
  [EPhysicalDimension.SURFACE_TENSION]: 'Napięcie powierzchniowe',
  [EPhysicalDimension.TEMPERATURE]: 'Temperatura',
  [EPhysicalDimension.THERMAL_EXPANSION]: 'Rozszerzalność cieplna',
  [EPhysicalDimension.THERMAL_RESISTANCE]: 'Opór cieplny',
  [EPhysicalDimension.TIME]: 'Czas',
  [EPhysicalDimension.TORQUE]: 'Moment obrotowy',
  [EPhysicalDimension.VELOCITY]: 'Prędkość',
  [EPhysicalDimension.VISCOSITY]: 'Lepkość',
  [EPhysicalDimension.VOLTAGE]: 'Napięcie',
  [EPhysicalDimension.VOLUME]: 'Objętość',
  [EPhysicalDimension.WORK]: 'Praca',
};

const CalendarEventType: Record<ECalendarEventType, string> = {
  [ECalendarEventType.ACTUAL_UNIT_DOWNTIME]: 'Rzeczywisty przestój (nieoczekiwany)',
  [ECalendarEventType.PLANNED_UNIT_DOWNTIME]: 'Planowany przestój',
  [ECalendarEventType.OPERATION_HOLD]: 'Wstrzymanie produkcji',
};

const MaintenanceEventType: Record<components['schemas']['MaintenanceEventType'], string> = {
  MAINTENANCE: 'Planowana konserwacja (cykliczna)',
};

const MaintenanceStateType: Record<components['schemas']['MaintenanceEventStateType'], string> = {
  ASSIGNED: 'Przydzielony',
  COMPLETED: 'Zakończony',
  IN_PROGRESS: 'W toku',
  IN_REVIEW: 'W trakcie przeglądu',
  PENDING: 'Oczekujące',
  REJECTED: 'Odrzucony',
};

export const Weekday: Record<TComponentValue<'Weekday'>, string> = {
  MONDAY: 'Poniedziałek',
  TUESDAY: 'Wtorek',
  WEDNESDAY: 'Środa',
  THURSDAY: 'Czwartek',
  FRIDAY: 'Piątek',
  SATURDAY: 'Sobota',
  SUNDAY: 'Niedziela',
};

const WeekdayShort: Record<TComponentValue<'Weekday'>, string> = {
  MONDAY: 'pon.',
  TUESDAY: 'wt.',
  WEDNESDAY: 'śr.',
  THURSDAY: 'czw.',
  FRIDAY: 'pt.',
  SATURDAY: 'sob.',
  SUNDAY: 'niedz.',
};

const AssetHierarchyLevel: Record<EHierarchy, string> = {
  [EHierarchy.CHAMBER]: generalTerm.chamber,
  [EHierarchy.COMPANY]: generalTerm.company,
  [EHierarchy.COMPONENT]: generalTerm.component,
  [EHierarchy.EQUIPMENT]: generalTerm.equipment,
  [EHierarchy.EQUIPMENT_GROUP]: generalTerm.equipmentGroup,
  [EHierarchy.AUTOMAT]: generalTerm.automat,
  [EHierarchy.AREA]: generalTerm.area,
  [EHierarchy.LOCATION]: generalTerm.location,
  [EHierarchy.ROOT]: generalTerm.root,
};

export const NotificationChannelType: Record<ENotificationChannelType, string> = {
  [ENotificationChannelType.EMAIL]: generalTerm.email,
  [ENotificationChannelType.IN_APP]: 'W aplikacji',
  [ENotificationChannelType.SMS]: generalTerm.sms,
};

export const SimulationStep: TComponentKeys<'SimulationStep'> & {
  surfaceHardnessSetpoint: string,
  shortCaseDepth: string,
} = {
  runtimeSeconds: 'Czas wykonania w sekundach',
  stageIndex: 'Indeks etapu',
  stageRuntimeSeconds: 'Czas wykonania sceny w sekundach',
  temperature: 'Temperatura',
  carbonPotential: 'Potencjał węglowy',
  nitrogenPotential: 'Potencjał azotowy',
  massFlowHydroCarbon: 'Przepływ masowy',
  carburizingDepth: 'Głębokość nawęglania',
  carburizingDepthSetPoint: 'Punkt nastawy głębokości nawęglania',
  carburizingDepthCarbonContent: 'Zawartość węgla na głębokości nawęglania',
  surfaceCarbon: 'Węgiel powierzchniowy',
  surfaceNitrogen: 'Azot powierzchniowy',
  surfaceCarbide: 'węglik powierzchniowy',
  effectiveCaseHardness: 'Efektywna twardość obudowy',
  carbideLimit: 'Granica węglików',
  surfaceHardnessSetpoint: 'Punkt nastawczy twardości powierzchni',
  sootLimit: 'Granica sadzy',
  caseDepth: 'Głębokość przypadku',
  shortCaseDepth: 'ECD',
  retainedAustenite: 'Austenit szczątkowy',
  compoundLayerThickness: 'Grubość warstwy złożonej',
  diffusionLayerThickness: 'Grubość warstwy dyfuzyjnej',
  nitridedHardnessDepth: 'NHD + 50HV',
  totalDiffusionDepth: 'Całkowita głębokość azotowania',
  carbonProfile: 'Profil węglowy w głąb',
  carbonProfileMin: 'Minimalna wartość profilu węglowego',
  carbonProfileMax: 'Maksymalna wartość profilu węglowego',
  nitrogenProfile: 'Profil azotu w głąb',
  nitrogenProfileMin: 'Minimalna wartość profilu azotu',
  nitrogenProfileMax: 'Maksymalna wartość profilu azotu',
  carbideProfile: 'Profil węglika spiekanego na głębokość',
  carbideProfileMin: 'Minimalna wartość profilu węglika',
  carbideProfileMax: 'Maksymalna wartość profilu węglika',
  hardnessProfile: 'Profil twardości na głębokości',
  hardnessProfileMin: 'Minimalna wartość profilu twardości',
  hardnessProfileMax: 'Maksymalna wartość profilu twardości',
  nSolubilityProfile: 'Rzeczywisty limit azotków',
  nitrideProfile: 'Profil azotku na głębokości',
  nitrideProfileMin: 'Minimalna wartość profilu azotku',
  nitrideProfileMax: 'Maksymalna wartość profilu azotku',
  profileXValues: 'Wartości wzdłuż profilu od zewnątrz do wewnątrz w mm',
};

const Asset: Partial<TComponentKeys<'Asset'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  hierarchyOrder: 'Pozycja na liście',
  hierarchyLevel: 'Poziom zasobów',
  parentId: 'Zasób nadrzędny',
};

const AssetCreate: Partial<TComponentKeys<'AssetCreate'>> = {
  ...Asset,
  permissionsRoleIds: 'Role umożliwiające udzielanie dostępu',
};

const CompanyCreate: Partial<TComponentKeys<'CompanyCreate'>> = {
  ...Asset,
  timezone: 'Strefa czasowa',
};

const ShiftHoursWindow: Partial<TComponentKeys<'ShiftHoursWindow'>> = {
  startsAt: 'Rozpoczyna się o',
  endsAt: 'Kończy się o',
};

const LocationShift: Partial<TComponentKeys<'LocationShift'>> = {
  displayName: 'Zmiana',
};

const LocationCreate: Partial<TComponentKeys<'LocationCreate'>> = {
  ...CompanyCreate,
};

const EquipmentCreate: Partial<TComponentKeys<'EquipmentCreate'>> = {
  ...Asset,
  equipmentPhysicalTypeId: 'Typ fizyczny',
  serialNumber: 'Numer seryjny',
  manufacturer: 'Producent',
  model: 'Model',
  ipAddress: 'Adres IP',
  chambers: 'Kancelaria',
  department: 'Dział',
};

const ChamberCreate: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...Asset,
  chamberType: 'Komorowy',
  heatingZoneCount: 'Liczba stref grzewczych',
  concurrentJobLimit: 'Maks. liczba jednoczesnych zadań',
  heatTreatmentCapabilities: 'Możliwości obróbki cieplnej',
};

const Chamber: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...ChamberCreate,
};

const CameraRegistryEntryCameraType: Record<components['schemas']['CameraRegistryEntry']['cameraType'], string> = {
  IP_CAMERA_SNAPSHOT: 'Zrzut obrazu z kamery IP',
  IP_CAMERA_STREAM: 'Strumień z kamery IP',
};

const CameraRegistryEntry: TComponentKeys<'CameraRegistryEntry'> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  assetId: generalTerm.asset,
  maxAgeThresholdSeconds: 'Czas przechowywania obrazu',
  staleThresholdSeconds: 'Oznacz obraz jako przestarzały po',
  cameraId: 'Kamera',
  cameraType: 'Typ kamery',
  cameraUrl: 'URL kamery',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
};

const AssetProfileStatus: Record<EAssetProfileStatus, string> = {
  [EAssetProfileStatus.COMPLETE]: 'Kompletny',
  [EAssetProfileStatus.INCOMPLETE]: 'Niekompletny',
  [EAssetProfileStatus.INVALID]: 'Nieważny',
};

const AssetProfileCombinator: Record<EAssetProfileCombinator, string> = {
  [EAssetProfileCombinator.ANY_OF]: 'Którykolwiek',
  [EAssetProfileCombinator.ALL_OF]: 'Wszystko',
  [EAssetProfileCombinator.ONE_OF]: 'Jeden z',
};

const ObservationType: Record<EObservationType, string> = {
  [EObservationType.LOWER_CONTROL_LIMIT_EXCEEDED]: 'Przekroczono dolną granicę kontrolną',
  [EObservationType.UPPER_CONTROL_LIMIT_EXCEEDED]: 'Przekroczono górną granicę kontrolną',
  [EObservationType.NEGATIVE_TREND_DETECTED]: 'Wykryto trend negatywny',
  [EObservationType.POSITIVE_TREND_DETECTED]: 'Wykryto pozytywny trend',
  [EObservationType.LOWER_CUSTOM_LIMIT_EXCEEDED]: 'Przekroczono dolny limit niestandardowy',
  [EObservationType.UPPER_CUSTOM_LIMIT_EXCEEDED]: 'Przekroczono górny limit niestandardowy',
};

const ChamberType: Record<EChamberType, string> = {
  [EChamberType.HOT]: 'Gorący',
  [EChamberType.ISO_THERMAL]: 'Izotermiczny',
  [EChamberType.QUENCH]: 'Gaszenie',
  [EChamberType.VACUM]: 'Próżnia',
  [EChamberType.LOADING]: 'Załadunek',
  [EChamberType.UNLOADING]: 'Rozładunek',
  [EChamberType.LOADING_UNLOADING]: 'Załadunek/Rozładunek',
};

const HeatTreatmentProcessType: Record<EHeatTreatmentProcessType, string> = {
  [EHeatTreatmentProcessType.ANNEALING]: 'Wyżarzanie',
  [EHeatTreatmentProcessType.QUENCHING]: 'Gaszenie',
  [EHeatTreatmentProcessType.NEUTRAL_HARDENING]: 'Utwardzanie neutralne',
  [EHeatTreatmentProcessType.CRYOGENIC_TREATMENT]: 'Obróbka kriogeniczna',
  [EHeatTreatmentProcessType.TEMPERING]: 'Ruszenie',
  [EHeatTreatmentProcessType.AGING]: 'Starzenie się',
  [EHeatTreatmentProcessType.CARBURIZING]: 'Nawęglanie',
  [EHeatTreatmentProcessType.CARBONITRIDING]: 'węgloazotowanie',
  [EHeatTreatmentProcessType.NITRIDING]: 'Azotowanie',
  [EHeatTreatmentProcessType.NITROCARBURIZING]: 'Azotonawęglanie',
  [EHeatTreatmentProcessType.PRE_OXIDATION]: 'Wstępne utlenianie',
  [EHeatTreatmentProcessType.POST_OXIDATION]: 'Po utlenianiu',
  [EHeatTreatmentProcessType.ACTIVATION]: 'Aktywacja',
};

const AppGroupSemanticDepartment: Record<EAppGroupSemanticDepartment, string> = {
  [EAppGroupSemanticDepartment.PRODUCTION_PLANNING]: 'Przygotowanie produkcji',
  [EAppGroupSemanticDepartment.OPERATIONS]: 'Produkcja',
  [EAppGroupSemanticDepartment.QUALITY_ASSURANCE]: 'Zapewnienie jakości',
  [EAppGroupSemanticDepartment.ANALYTICS]: 'Analityka',
  [EAppGroupSemanticDepartment.MASTER_DATA]: 'Dane podstawowe',
  [EAppGroupSemanticDepartment.ADMINISTRATION]: 'Administracja',
};

const AppGroupSemanticTopic: Record<EAppGroupSemanticTopic, string> = {
  [EAppGroupSemanticTopic.OPERATION]: 'Działanie',
  [EAppGroupSemanticTopic.DATA]: 'Dane/Wgląd',
  [EAppGroupSemanticTopic.MAINTENANCE]: 'Konserwacja',
  [EAppGroupSemanticTopic.PROCESS]: 'Proces',
  [EAppGroupSemanticTopic.CONFIGURATION]: 'Konfiguracja',
};

const AppId: Record<EAppId, string> = {
  [EAppId.AI_ASSISTANT]: 'Asystent AI',
  [EAppId.ALARM_STATISTICS]: 'Statystyki alarmów',
  [EAppId.ANOMALY_DETECTION]: 'Wykrywanie anomalii',
  [EAppId.ASSET_MANAGEMENT]: 'Zarządzanie zasobami',
  [EAppId.CERTIFICATION]: 'Audyt i jakość',
  [EAppId.CUSTOMER]: 'Klient',
  [EAppId.DEPRECATED_MODEL_PROCESS]: 'Kreator procesów modelowych',
  [EAppId.GRAFANA]: 'Grafana',
  [EAppId.HISTORY_AND_TRENDS]: 'Historia i trendy',
  [EAppId.LIVE]: 'Aplikacja na żywo',
  [EAppId.MATERIALS]: 'Materiały',
  [EAppId.MODEL_BUILDER]: 'Kreator modeli',
  [EAppId.OPERATION_CALENDAR]: 'Kalendarz operacji',
  [EAppId.ORDERS]: 'Zlecenia',
  [EAppId.PARTS]: 'Części',
  [EAppId.PERFORMANCE_DASHBOARD]: 'Panel wydajności',
  [EAppId.PRODUCTION_LOGS]: 'Dzienniki produkcji',
  [EAppId.PRODUCTION_SCHEDULER]: 'Harmonogram produkcji',
  [EAppId.PRODUCTION_STATISTICS]: 'Statystyki produkcji',
  [EAppId.PT9800_CONFIGURATION]: 'Konfiguracja Protherm',
  [EAppId.QMULUS_DASHBOARD]: 'Panele',
  [EAppId.RECIPE]: 'Receptury',
  [EAppId.RECIPE_ON_DEMAND]: 'Receptura na żądanie',
  [EAppId.REMOTE_CONTROL]: 'Zdalne sterowanie',
  [EAppId.SHOP_LAYOUT]: 'Układ hali',
  [EAppId.SIMULATOR]: 'Symulatory',
  [EAppId.TENANT_SETTINGS]: 'Ustawienia',
  [EAppId.UPC_PRO]: 'UPC Toolbox',
  [EAppId.USER_MANAGEMENT]: 'Zarządzanie użytkownikami',
};

const CarburizingSimulationPartShape: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Stal okrągła',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Stal sześciokątna lub ośmiokątna',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Stal kwadratowa lub płaska',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Stal lita (średnica wewnętrzna tarczy < 200 mm)',
  [ESimulatorSimulationPartShape.RING]: 'Pierścień (średnica wewnętrzna tarczy > 200 mm)',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Średnica wewnętrzna korpusu cylindrycznego < 80 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Średnica wewnętrzna korpusu cylindrycznego < 200 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Średnica wewnętrzna korpusu cylindrycznego > 200 mm',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Pusty korpus zamknięty z jednej lub dwóch stron',
};

const CarburizingSimulationPartShapeSize: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Średnica',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Odległość między dwoma stronami',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Długość najmniejszego boku',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Długość najmniejszego boku',
  [ESimulatorSimulationPartShape.RING]: 'Długość najmniejszego boku',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Grubość ścianki',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Grubość ścianki',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Grubość ścianki',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Grubość ścianki',
};

const CarburizingSimulatorRecipeType: Record<ESimulatorRecipeType, string> = {
  [ESimulatorRecipeType.CARBURIZING]: 'Nawęglanie [C]',
  [ESimulatorRecipeType.CARBONITRIDING]: 'węgloazotowanie [CN]',
  [ESimulatorRecipeType.LOW_PRESSURE_CARBURIZING]: 'Nawęglanie niskociśnieniowe [LPC]',
  [ESimulatorRecipeType.NITRIDING]: 'Azotowanie [N]',
  [ESimulatorRecipeType.NITROCARBURIZING]: 'Azotonawęglanie [NC]',
};

const MaterialPropertySpectrum: Record<keyof components['schemas']['MaterialPropertySpectrum']['avg'], string> = {
  alloyFactorCAustenite: 'Współczynnik stopu [węgiel]',
  carbideLimit: 'Granica węglików [%aC=1]',
  alloyFactorNAustenite: 'Współczynnik stopu [Azot]',
  nitrideLimit: 'Granica azotków [%FexNy]',
  coreHardness: 'Twardość rdzenia bez obróbki',
  maxHardness: 'Maksymalna twardość martenzytu',
  ac1: 'AC1',
  ac3: 'AC3',
  ms: 'Początek martenzytu',
  mf: 'Wykończenie martenzytyczne',
};

const PartMaterialProperties: TComponentKeys<'PartMaterialProperties'> = {
  ...MaterialPropertySpectrum,
  surfaceCarbon: 'Węgiel powierzchniowy',
  referenceCarbon: 'Węgiel referencyjny',
};

const SimulatorRecipe: TComponentKeys<'SimulatorRecipe'> = {
  description: generalTerm.description,
  displayName: generalTerm.name,
  isGasCompositionConstant: 'Stały skład gazu',
  gasComposition: generalTerm.gasComposition,
  recipeType: 'Rodzaj obróbki cieplnej',
  simulationValuesType: 'Typ',
  simulatorRecipeId: '',
  simulatorPartId: '',
  materialCalculationModelId: 'Model obliczeniowy materiałów',
  stages: '',
  userDefinedAlloyFactor: generalTerm.alloyFactor,
  userDefinedCarbideLimit: generalTerm.carbideLimit,
  userDefinedCarbonContent: generalTerm.carbonContent,
  userDefinedCarburizingDepth: generalTerm.carburizingDepth,
  userDefinedReferenceCarbon: generalTerm.referenceCarbon,
};

const NotificationTable: Record<ESubscriptionTreeLabel, Record<'event' | 'description', string>> = {
  [ESubscriptionTreeLabel.ALARMS]: {
    event: 'Alarmy',
    description: 'Otrzymuj powiadomienie, gdy wystąpi zdarzenie alarmowe o dowolnym stopniu ważności',
  },
  [ESubscriptionTreeLabel.ALARM_OCCURRED]: {
    event: 'Alarm',
    description: 'Powiadomienie o zdarzeniach alarmowych',
  },
  [ESubscriptionTreeLabel.JOBS]: {
    event: 'Praca',
    description: 'Otrzymuj powiadomienie za każdym razem, gdy zmieni się status zadania',
  },
  [ESubscriptionTreeLabel.JOB_FINISHED]: {
    event: 'Zadanie zakończone',
    description: 'Praca została ukończona.',
  },
  [ESubscriptionTreeLabel.JOB_STARTED]: {
    event: 'Praca rozpoczęta',
    description: 'Praca została rozpoczęta',
  },
  [ESubscriptionTreeLabel.MONITORING]: {
    event: 'Monitorowanie',
    description: 'Otrzymuj powiadomienia o zdarzeniach monitorowania Qmulus',
  },
  [ESubscriptionTreeLabel.TIMESERIES_LAG_DETECTED]: {
    event: 'Wykryto opóźnienie szeregu czasowego',
    description: 'Synchronizacja pomiędzy źródłami danych opóźnia się o ponad 30 minut',
  },
  [ESubscriptionTreeLabel.NEW_QMULUS_RELEASE]: {
    event: 'Nowa wersja Qmulusa',
    description: 'Wydano nową wersję Qmulusa',
  },
  [ESubscriptionTreeLabel.ANOMALIES_AND_OBSERVATIONS]: {
    event: 'Anomalie i obserwacje',
    description: 'Otrzymuj powiadomienie za każdym razem, gdy zostanie zaobserwowana anomalia.',
  },
  [ESubscriptionTreeLabel.CONTROL_LEVEL_EXCEEDED]: {
    event: 'Poziom kontroli przekroczony',
    description: 'Przekroczono poziom kontroli czujnika',
  },
  [ESubscriptionTreeLabel.TREND_DETECTED]: {
    event: 'Wykryto trend',
    description: 'Wykryto nowy trend w odczytach czujników',
  },
  [ESubscriptionTreeLabel.DRIFT_DETECTED]: {
    event: 'Wykryto dryf',
    description: 'Odczyty czujników odbiegają od wartości odniesienia',
  },
  [ESubscriptionTreeLabel.OPERATION_MAINTENANCE]: {
    event: 'Eksploatacja i konserwacja',
    description: 'Otrzymuj powiadomienia o zdarzeniach związanych z eksploatacją i konserwacją',
  },
  [ESubscriptionTreeLabel.MAINTENANCE_OCCURRENCE_STATE_CHANGED]: {
    event: 'Zmieniono stan konserwacji',
    description: 'Aktualny stan zadania konserwacyjnego uległ zmianie',
  },
};

const SubscriptionTreeNode: Partial<TComponentKeys<'SubscriptionTreeNode'>> = {
  notificationMetadataId: generalTerm.description,
  label: generalTerm.event,
  subscriptions: '',
};

const CarburizingRecipeSimulationValuesType: Record<ESimulatorRecipeSimulationValuesType, string> = {
  [ESimulatorRecipeSimulationValuesType.AVG_VAL]: 'Średnia',
  [ESimulatorRecipeSimulationValuesType.COMP_VAL]: 'Kom',
  [ESimulatorRecipeSimulationValuesType.USER_VAL]: 'Zdefiniowane przez użytkownika',
};

const DataFormat: Record<EDataFormat, string> = {
  [EDataFormat.TIME_SERIES]: 'Szereg czasowy',
  [EDataFormat.JOB_AGGREGATE]: 'Wartość pracy',
  [EDataFormat.EVENT]: 'Wydarzenie',
  [EDataFormat.ALARM_EVENT]: 'Alarm',
  [EDataFormat.BLOB]: 'Duży obiekt binarny (BLOB)',
};

const ObservationTypeHeader: Record<string, string> = {
  name: 'Nazwa typu obserwacji',
};

const AssetUtilization: Partial<TComponentKeys<'AssetUtilization'>> = {
  assetDisplayName: generalTerm.asset,
  noProductionSeconds: 'Brak produkcji',
  actualProductionTimeSeconds: 'Rzeczywisty czas produkcji',
  plannedProductionTimeSeconds: 'Planowany czas produkcji',
  actualUnitDowntimeSeconds: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimeSeconds: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldSeconds: CalendarEventType.OPERATION_HOLD,
  noProductionPercentage: 'Brak produkcji',
  actualProductionTimePercentage: 'Rzeczywisty czas produkcji',
  plannedProductionTimePercentage: 'Planowany czas produkcji',
  actualUnitDowntimePercentage: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimePercentage: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldPercentage: CalendarEventType.OPERATION_HOLD,
  effectiveTargetProductivityPercentage: 'Docelowa produktywność',
};

const AuditSpecificationResultOverview: TComponentKeys<'AuditSpecificationResultOverview'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  auditResultId: 'Numer raportu',
  auditedAtUtc: generalTerm.date,
  auditedByUserName: 'Zgłaszający',
  jobName: generalTerm.jobDisplayName,
  recipeDisplayName: generalTerm.recipe,
  jobId: '',
};

const AuditEnvelopeNonPassedReasons: Record<components['schemas']['AuditEnvelopeNonPassedReasons'], string> = {
  no_start_point_found: 'Nie znaleziono punktu początkowego',
  values_out_of_band: 'Wartości poza pasmem',
  max_duration_exceeded: 'Przekroczono maksymalny czas trwania',
  min_duration_not_reached: 'Nie osiągnięto minimalnego czasu trwania',
  job_is_running: 'Zadanie jest uruchomione',
  meanings_missing: 'Brak dostępnych sygnałów ze zmapowanymi znaczeniami dla zasobu.',
  no_data: 'Brak danych',
  data_ended_unexpected: 'Dane nieoczekiwanie zakończyły się',
};

const Part: TComponentKeys<'Part'> = {
  hasAllRequiredAttributes: 'Wszystkie wymagane atrybuty uzupełnione',
  displayName: generalTerm.part,
  description: 'Opis części',
  materialId: generalTerm.material,
  customerId: generalTerm.customer,
  materialDisplayName: generalTerm.material,
  treatmentRemarks: 'Uwaga dotycząca obróbki',
  customerDisplayName: generalTerm.customer,
  drawingReference: 'Rysunek',
  isOnetime: 'Jednorazowa część',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  images: 'Obrazy',
  partId: generalTerm.partId,
  attributes: 'Atrybuty części',
  partRevisionId: generalTerm.revision,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  lastModifiedByUserId: generalTerm.lastModifiedBy,
  lastModifiedByUserDisplayName: generalTerm.lastModifiedBy,
  createdAtUtc: generalTerm.createdAt,
  createdByUserId: generalTerm.createdBy,
  createdByUserDisplayName: generalTerm.createdBy,
  isDeleted: generalTerm.showDeleted,
};

const PartDetails: TComponentKeys<'PartDetails'> = {
  ...Part,
  assetQualifications: 'Kwalifikowane aktywa i receptury',
};

const Customer: TComponentKeys<'Customer'> = {
  city: 'Miasto',
  contacts: 'Kontakty',
  country: 'Kraj',
  customerId: 'Firma',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  postalCode: 'Kod pocztowy',
  state: 'Województwo',
  street: 'Ulica',
  displayName: 'Firma',
  email: 'Poczta',
  phone: 'Telefon',
  cellphone: 'Telefon komórkowy',
  fax: 'Faks',
};

const Material: Partial<TComponentKeys<'Material'>> = {
  displayName: 'Nazwa materiału',
  materialComposition: 'Skład materiału',
  materialGroups: 'Grupowanie niestandardowe',
  materialNames: 'Nazwy materiałów',
};

const MaterialNamingSystem: Partial<TComponentKeys<'MaterialNamingSystem'>> = {
  displayName: 'Nazwa',
};

const ChartAssessmentTestRequirementType: Record<components['schemas']['ChartAssessmentTestRequirementType'], string> = {
  setpoints_greater_than_measures: 'Środki < Wartości zadane',
  setpoints_greater_than_or_equal_to_measures: 'Środki ≤ Punkty nastawcze',
  setpoints_less_than_measures: 'Pomiary > Wartości zadane',
  setpoints_less_than_or_equal_to_measures: 'Środki ≥ Punkty nastawcze',
};

const User: Partial<TComponentKeys<'User'>> = {
  displayName: generalTerm.displayName,
  firstName: 'Imię',
  lastName: 'Nazwisko',
  email: 'E-mail',
  rolesDisplayName: 'Role',
};

const OpenAPIPrimitiveType: Record<components['schemas']['OpenAPIPrimitiveType'], string> = {
  boolean: 'Boole\'a',
  integer: 'Liczba całkowita',
  number: 'Liczba całkowita zmiennoprzecinkowa',
  string: 'Tekst',
};

const ProductionStatistics: Partial<TComponentKeys<'ProductionStatistics'>> = {
  displayName: generalTerm.name,
  earliestJobStartedAtUtc: 'Najwcześniejszy czas rozpoczęcia pracy',
  latestJobFinishedAtUtc: 'Ostatni czas zakończenia zadania',
  jobCnt: 'Liczba miejsc pracy',
};

const CsvSeperator: Record<components['schemas']['CsvSeperator'], string> = {
  comma: 'Przecinek',
  semicolon: 'Średnik',
  tab: 'Patka',
  pipe: 'Rura',
  space: 'Przestrzeń',
};

const CsvDatetimeFormat: Record<components['schemas']['CsvDatetimeFormat'], string> = {
  iso_8601: 'ISO 8601',
  unix_timestamp: 'UNIX',
  us: 'NAS',
};

const CsvQuoteStyle: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Niezbędny',
  always: 'Zawsze',
  non_numeric: 'Nienumeryczne',
  never: 'Nigdy',
};

const CsvQuoteStyleHint: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Są one niezbędne, gdy pola zawierają cudzysłów, separator lub zakończenie rekordu. Cudzysłowy są również niezbędne podczas zapisywania pustego rekordu (który jest nieodróżnialny od rekordu z jednym pustym polem). Jest to ustawienie domyślne.',
  always: 'To umieszcza cudzysłów wokół każdego pola. Zawsze.',
  non_numeric: 'To umieszcza cudzysłowy wokół wszystkich pól, które nie są liczbami. Mianowicie, podczas zapisywania pola, które nie jest parsowane jako prawidłowa liczba zmiennoprzecinkowa lub całkowita, cudzysłowy zostaną użyte, nawet jeśli nie są absolutnie konieczne.',
  never: 'Pola nigdy nie są ujmowane w cudzysłów, nawet jeśli powoduje to powstanie nieprawidłowych danych CSV (np.',
};

const CsvLineTerminator: Record<components['schemas']['CsvLineTerminator'], string> = {
  posix: 'POSIX',
  windows: 'Okna',
};

const CsvQuoteCharacter: Record<components['schemas']['CsvQuoteCharacter'], string> = {
  single_quote: 'Pojedynczy cytat',
  double_quote: 'Podwójny cudzysłów',
};

const PartAssetRecipeQualification: Partial<TComponentKeys<'PartAssetRecipeQualification'>> = {
  assetIds: generalTerm.asset,
  maxNumberOfPartsPerRost: 'Maksymalna liczba części na rosę',
  recipeDisplayName: generalTerm.recipe,
  recipeId: generalTerm.recipe,
  loadingSchemeId: 'Schemat załadunku',
};

const AuditSpecification: TComponentKeys<'AuditSpecification'> = {
  auditSpecificationId: 'Identyfikator specyfikacji audytu',
  description: 'Opis',
  displayName: 'Nazwa specyfikacji',
  envelopes: 'Liczba kopert',
  revision: 'Identyfikator wersji',
  isOneTimeSpec: '',
};

const AlarmCollection: TComponentKeys<'AlarmCollection'> = {
  dataId: 'Identyfikator danych',
  assetId: generalTerm.asset,
  displayName: generalTerm.displayName,
  eventType: 'Typ alarmu',
  occurrenceData: generalTerm.date,
  severity: 'Typ alarmu',
};

const AlarmEvent: TComponentKeys<'AlarmEvent'> = {
  occurredAtUtc: 'Start',
  clearedAtUtc: 'Koniec',
  acknowledgedAtUtc: 'Potwierdzono',
  durationSeconds: 'Czas trwania',
};

const CounterAction: Partial<TComponentKeys<'CounterAction'>> = {
  displayName: 'Kontrakcja',
  description: generalTerm.description,
};

const DataSource: TComponentKeys<'DataSource'> = {
  dataSourceId: generalTerm.dataSourceId,
  displayName: generalTerm.displayName,
  dataSourceType: 'Typ źródła danych',
  description: generalTerm.description,
  timezone: 'Strefa czasowa',
  connectionDetails: 'Szczegóły',
  assetAssociations: 'Powiązania z zasobami',
};

const DataSourceAssociation: TComponentKeys<'DataSourceAssociation'> = {
  assetId: generalTerm.assetId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
};

const Metadata: Partial<TComponentKeys<'Metadata'>> = {
  dataId: 'dataId',
  assetId: generalTerm.asset,
  dataSourceId: generalTerm.dataSourceId,
  color: generalTerm.color,
  dataFormat: 'Format danych',
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
  description: generalTerm.description,
  displayName: generalTerm.name,
  guaranteedColor: generalTerm.color,
  isHidden: 'Widoczność',
  meaningId: 'Oznaczający',
  meaningInstanceNumber: 'przykład',
  significantDigits: 'Cyfry znaczące',
  unitOfMeasureId: 'Jednostka',
  scaleType: 'Typ osi',
};

const MetadataUpdate: Partial<TComponentKeys<'MetadataUpdate'>> = {
  ...Metadata,
  significantDigitsOverwrite: Metadata.significantDigits,
};

const AlarmDefinition: Partial<TComponentKeys<'AlarmDefinition'>> = {
  ...Metadata,
  severity: AlarmCollection.severity,
};

const PartAttribute: Partial<TComponentKeys<'PartAttribute'>> & {
  value: string,
} = {
  displayName: 'Atrybuty',
  unitOfMeasureId: 'Jednostka',
  value: 'Wartość',
  valueType: 'Typ wartości',
  isBuiltIn: 'Wbudowany',
  isRequired: 'Wymagany',
  isVisible: Metadata.isHidden!,
};

const Sample: Partial<TComponentKeys<'Sample'>> = {
  displayName: 'Próbka',
};

const TableAssessmentTest: Partial<TComponentKeys<'TableAssessmentTest'>> = {
  displayName: generalTerm.name,
  isOverwritten: generalTerm.overwrite,
};

const TableAssessmentTestItem: Partial<Record<keyof components['schemas']['TableAssessmentTest']['items'][number], string>> = {
  displayName: generalTerm.name,
  requirement: generalTerm.requirement,
  result: generalTerm.result,
};

const AssessmentTestResultType = 'Wynik testu';

const ChartAssessmentTest: Partial<TComponentKeys<'ChartAssessmentTest'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  xUnitId: 'Jednostka X',
  yUnitId: 'Jednostka Y',
  requirement: 'Sprawdź',
  isOverwritten: generalTerm.overwrite,
};

const FileAssessmentTest: Partial<TComponentKeys<'FileAssessmentTest'>> = {
  displayName: generalTerm.name,
  result: 'Wynik testu',
  resultComment: 'Komentarz',
  isOverwritten: generalTerm.overwrite,
};

const ReportOverview: Partial<TComponentKeys<'ReportOverview'>> = {
  reportId: generalTerm.reportId,
  reporterDisplayName: generalTerm.reporter,
  jobId: generalTerm.jobId,
  jobDisplayName: generalTerm.job,
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  assetLocation: generalTerm.location,
  recipeDisplayName: generalTerm.recipe,
  partId: generalTerm.partId,
  partDisplayName: generalTerm.part,
  partCustomerDisplayName: generalTerm.customer,
  partMaterialDisplayName: generalTerm.material,
  partDrawingReference: generalTerm.drawingReference,
  orderNumbers: 'Zamówienie',
  isAutogenerated: 'Zaimportowano z systemu obcego',
  orderCreatedAtUtc: 'Data zamówienia',
  isDeleted: generalTerm.showDeleted,
};

const ReportBlockAlarmsTemplateVariable: Record<TComponentsSchema['ReportBlockAlarmsTemplateVariable'], string> = {
  alarms: 'Alarmy',
};

const ReportBlockAuditEnvelopesTemplateVariable: Record<TComponentsSchema['ReportBlockAuditEnvelopesTemplateVariable'], string> = {
  envelopes: 'Koperty',
};

const ReportBlockAuditReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockAuditReportHeaderTemplateVariable'], string> = {
  asset_name: 'Zasób',
  audit_specification_display_name: 'Specyfikacja',
  audited_at: 'Data audytu',
  auditor_display_name: 'Audytor',
  auditor_remark: 'Uwagi audytora',
  description: 'Opis',
  duration_seconds: 'Czas trwania',
  finished_at: 'Czas końcowy',
  load_number: 'Numer zadania',
  location_name: 'Lokalizacja',
  recipe_description: 'Opis receptury',
  recipe_name: 'Receptura',
  report_number: 'Numer raportu',
  started_at: 'Czas rozpoczęcia',
};

const ReportBlockJobDetailsHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockJobDetailsHeaderTemplateVariable'], string> = {
  asset_name: 'Zasób',
  description: 'Opis',
  duration_seconds: 'Czas trwania',
  finished_at: 'Czas końcowy',
  load_number: 'Numer zadania',
  location_name: 'Lokalizacja',
  recipe_description: 'Opis receptury',
  recipe_name: 'Receptura',
  started_at: 'Czas rozpoczęcia',
};

const ReportBlockJobPartsField: Record<TComponentsSchema['ReportBlockJobPartsField'], string> = {
  customer: generalTerm.customer,
  description: generalTerm.description,
  displayName: generalTerm.part,
  drawingReference: generalTerm.drawingReference,
  material: generalTerm.material,
  order: 'Zamówienie',
  partCount: 'Ilość',
  treatmentRemarks: 'Uwagi do obróbki',
};

const ReportBlockJobPartsTemplateVariable: Record<TComponentsSchema['ReportBlockJobPartsTemplateVariable'], string> = {
  attribute_totals: 'Suma każdego atrybutu',
  attribute_unit_symbols: 'Jednostka każdego atrybutu',
  columns: 'Kolumny tabeli',
  has_part_read_permission: 'Uprawnienie do odczytu części',
  part_attributes: 'Atrybuty części',
  part_count_total: 'Łączna ilość',
  parts: 'Części',
};

const ReportBlockJobStagesTemplateVariable: Record<TComponentsSchema['ReportBlockJobStagesTemplateVariable'], string> = {
  has_stage_read_permission: 'Uprawnienie do odczytu etapów',
  stages: 'Etapy',
};

const ReportBlockQualityReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockQualityReportHeaderTemplateVariable'], string> = {
  asset_name: 'Zasób',
  customer_display_name: generalTerm.customer,
  description: 'Opis',
  duration_seconds: 'Czas trwania',
  finished_at: 'Czas końcowy',
  load_number: 'Numer zadania',
  location_name: 'Lokalizacja',
  material_display_name: generalTerm.material,
  part_display_name: generalTerm.part,
  part_drawing_reference: generalTerm.drawingReference,
  recipe_description: 'Opis receptury',
  recipe_name: 'Receptura',
  report_number: 'Numer raportu',
  reporter_display_name: generalTerm.reporter,
  started_at: 'Czas rozpoczęcia',
};

const ReportBlockQualitySamplesTemplateVariable: Record<TComponentsSchema['ReportBlockQualitySamplesTemplateVariable'], string> = {
  samples: 'Próbki',
};

const ReportBlockRecipeTemplateVariable: Record<TComponentsSchema['ReportBlockRecipeTemplateVariable'], string> = {
  does_include_setpoints: 'Każda wartość zadana w osobnym wierszu',
  does_include_signal_legend: 'Legenda sygnałów',
  specification: 'Specyfikacja obróbki',
};

const ReportBlockSignaturesTemplateVariable: Record<TComponentsSchema['ReportBlockSignaturesTemplateVariable'], string> = {
  titles: 'Tytuły pól podpisów',
};

const ReportBlockId: Record<TReportBlockId, string> = {
  alarms: 'Alarmy',
  auditEnvelopes: 'Koperty audytu',
  auditReportHeader: 'Nagłówek raportu z audytu',
  jobDetailsHeader: 'Szczegóły zadania',
  jobImages: 'Zdjęcia zadania',
  jobParts: 'Części',
  jobStages: 'Etapy wsadu',
  qualityReportHeader: 'Nagłówek raportu jakości',
  qualitySamples: 'Próbki i badania',
  recipe: 'Receptura',
  signatures: 'Podpisy',
  timeseriesChart: 'Wykres szeregów czasowych',
};

const ReportComponentIdType: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Audyt: Koperta',
  auditEnvelopeSections: 'Audyt: Odcinki koperty',
  auditResult: 'Audyt: Wynik',
  formatQuantity: 'Wartość z jednostką',
  formatValue: 'Wartość',
  keyValueGrid: 'Siatka wartości',
  keyValuePair: 'Wartość z etykietą',
  prothermBatchRecipe: 'Protherm: Receptura wsadowa',
  prothermBeltRecipe: 'Protherm: Receptura taśmowa',
  qualityChartTest: 'Jakość: Badanie z wykresem',
  qualityFileTest: 'Jakość: Badanie z plikiem',
  qualityImageTest: 'Jakość: Badanie z obrazem',
  qualitySample: 'Jakość: Próbka',
  qualityTableTest: 'Jakość: Badanie tabelaryczne',
  qualityTest: 'Jakość: Badanie',
  qualityTestResult: 'Jakość: Wynik badania',
  table: 'Tabela',
  warning: 'Ostrzeżenie',
};

const ReportComponentIdTypeDescription: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Pokazuje kopertę audytu z jej czujnikami, pasmem i czasem trwania, wykresem czujników, odcinkami i oceną.',
  auditEnvelopeSections: 'Wyświetla odcinki koperty, w których wartości pozostały w paśmie, wraz z ich czasami, wartościami i wynikiem.',
  auditResult: 'Pokazuje, czy koperta została zaliczona, niezaliczona lub jest niejednoznaczna.',
  formatQuantity: 'Formatuje liczbę z jej jednostką, np. 1575 °C. Jednostki takie jak °C, %, bar lub m³/h są składane jak w druku; inne jednostki następują po liczbie jako tekst. Liczba jest zaokrąglana do podanej liczby miejsc po przecinku, domyślnie do dwóch.',
  formatValue: 'Formatuje pojedynczą wartość: datę i godzinę, liczby, tak lub nie oraz tekst. Tabele i wartości z etykietą formatują nim każdą wartość.',
  keyValueGrid: 'Układa wartości z etykietą w wierszach, np. szczegóły zadania. Każdy wiersz zawiera pary klucza i wartości.',
  keyValuePair: 'Pokazuje wartość pod jej etykietą. Wartość jest formatowana przez komponent wartości.',
  prothermBatchRecipe: 'Pokazuje kroki receptury wsadowej Protherm z wartościami zadanymi, wyjściami cyfrowymi, warunkami zakończenia i czasem trwania, a opcjonalnie nazwy wyjść cyfrowych i warunków.',
  prothermBeltRecipe: 'Pokazuje strefy receptury taśmowej Protherm z ich pozycją w piecu, a opcjonalnie ich wartości zadane z tolerancjami i czasem alarmu.',
  qualityChartTest: 'Pokazuje wykres wartości zadanych i pomiarów badania z wykresem oraz tabelę ich wartości.',
  qualityFileTest: 'Pokazuje nazwę pliku badania z plikiem.',
  qualityImageTest: 'Pokazuje obraz badania z obrazem.',
  qualitySample: 'Pokazuje próbkę raportu jakości z jej polami niestandardowymi i badaniami.',
  qualityTableTest: 'Pokazuje cechy badania tabelarycznego z ich wymaganiem i zmierzonym wynikiem.',
  qualityTest: 'Pokazuje badanie z jego nazwą, wynikiem i komentarzem oraz szczegółami jego rodzaju.',
  qualityTestResult: 'Pokazuje, czy badanie zostało zaliczone, czy nie.',
  table: 'Pokazuje tabelę, która jest kontynuowana na kolejnych stronach. Każda kolumna ma nagłówek, opcjonalnie jednostkę, udział w szerokości, swoje komórki i opcjonalnie sumę.',
  warning: 'Wyróżnia komunikat, np. że nie udało się załadować danych.',
};

const ReportComponentAuditEnvelopeParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeParameter'], string> = {
  envelope: 'Koperta',
};

const ReportComponentAuditEnvelopeSectionsParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeSectionsParameter'], string> = {
  envelope: 'Koperta',
};

const ReportComponentAuditResultParameter: Record<TComponentsSchema['ReportComponentAuditResultParameter'], string> = {
  result: 'Wynik',
};

const ReportComponentFormatQuantityParameter: Record<TComponentsSchema['ReportComponentFormatQuantityParameter'], string> = {
  decimal_places: 'Miejsca po przecinku',
  unit: 'Jednostka',
  value: 'Wartość',
};

const ReportComponentFormatValueParameter: Record<TComponentsSchema['ReportComponentFormatValueParameter'], string> = {
  value: 'Wartość',
};

const ReportComponentKeyValueGridParameter: Record<TComponentsSchema['ReportComponentKeyValueGridParameter'], string> = {
  rows: 'Wiersze',
};

const ReportComponentKeyValuePairParameter: Record<TComponentsSchema['ReportComponentKeyValuePairParameter'], string> = {
  key: 'Etykieta',
  value: 'Wartość',
};

const ReportComponentProthermBatchRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBatchRecipeParameter'], string> = {
  does_include_setpoints: 'Każda wartość zadana w osobnym wierszu',
  does_include_signal_legend: 'Legenda sygnałów',
  specification: 'Receptura',
};

const ReportComponentProthermBeltRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBeltRecipeParameter'], string> = {
  does_include_setpoints: 'Każda wartość zadana w osobnym wierszu',
  does_include_signal_legend: 'Legenda sygnałów',
  specification: 'Receptura',
};

const ReportComponentQualityChartTestParameter: Record<TComponentsSchema['ReportComponentQualityChartTestParameter'], string> = {
  test: 'Badanie',
};

const ReportComponentQualityFileTestParameter: Record<TComponentsSchema['ReportComponentQualityFileTestParameter'], string> = {
  test: 'Badanie',
};

const ReportComponentQualityImageTestParameter: Record<TComponentsSchema['ReportComponentQualityImageTestParameter'], string> = {
  test: 'Badanie',
};

const ReportComponentQualitySampleParameter: Record<TComponentsSchema['ReportComponentQualitySampleParameter'], string> = {
  sample: 'Próbka',
};

const ReportComponentQualityTableTestParameter: Record<TComponentsSchema['ReportComponentQualityTableTestParameter'], string> = {
  test: 'Badanie',
};

const ReportComponentQualityTestParameter: Record<TComponentsSchema['ReportComponentQualityTestParameter'], string> = {
  test: 'Badanie',
};

const ReportComponentQualityTestResultParameter: Record<TComponentsSchema['ReportComponentQualityTestResultParameter'], string> = {
  result: 'Wynik',
};

const ReportComponentTableParameter: Record<TComponentsSchema['ReportComponentTableParameter'], string> = {
  columns: 'Kolumny',
};

const ReportComponentWarningParameter: Record<TComponentsSchema['ReportComponentWarningParameter'], string> = {
  message: 'Komunikat',
};

const ReportScopeType: Record<TComponentsSchema['ReportScopeType'], string> = {
  assetTimeRange: 'Zakres czasu zasobu',
  auditResultReference: 'Audyt',
  jobReference: 'Zadanie',
  qualityReportReference: 'Raport jakości',
};

const ReportTemplateIdType: Record<TComponentsSchema['ReportTemplateIdType'], string> = {
  '__base__': 'Układ bazowy',
  'qmulus.audit': 'Raport z audytu',
  'qmulus.quality': 'Raport jakości',
  'qmulus.report_builder.heat_treatment_report': 'Raport z obróbki cieplnej',
};

const ReportTemplateKind: Record<TComponentsSchema['ReportTemplateKind'], string> = {
  blocks: 'Bloki',
  template: 'Szablon',
};

const ReportTemplateUpsert: Partial<TComponentKeys<'ReportTemplateUpsert'>> = {
  header: 'Nagłówek',
  footer: 'Stopka',
  content: 'Treść',
  blocks: 'Bloki',
};

const ReportTemplateHeader: Partial<TComponentKeys<'ReportTemplateHeader-Input'>> = {
  height: 'Wysokość',
  contentLeft: 'Treść po lewej',
  contentCenter: 'Treść pośrodku',
  contentRight: 'Treść po prawej',
};

const ReportTemplateFooter: Partial<TComponentKeys<'ReportTemplateFooter-Input'>> = {
  contentLeft: 'Treść po lewej',
  contentCenter: 'Treść pośrodku',
  contentRight: 'Treść po prawej',
};

const Job: TComponentKeys<'Job'> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.jobDisplayName,
  durationSeconds: 'Czas trwania',
  description: generalTerm.description,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  finishedOverflowAtUtc: 'Rzeczywisty czas zakończenia',
  treatmentSpecificationDisplayName: generalTerm.recipe,
  hasAlarms: 'Alarmy',
  jobVerdict: 'Ocena',
  parentJobId: 'Przepływ procesu',
  isProductive: 'Oznacz zadanie jako produktywne',
  status: 'Stan',
  plannedStartAtUtc: 'Planowany czas rozpoczęcia',
  plannedFinishAtUtc: 'Planowany czas zakończenia',
  plannedDurationSeconds: 'Planowane trwanie',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  startedBy: 'Uruchomione przez',
  treatmentSpecificationId: generalTerm.recipe,
  treatmentSpecificationRevision: generalTerm.recipe,
  jobId: generalTerm.jobId,
  assetHierarchyLevel: generalTerm.asset,
  previousJobId: 'Poprzednie zadanie',
  nextJobId: 'Następne zadanie',
  parentAssetId: generalTerm.asset,
  startedOrPlannedTimeAtUtc: generalTerm.jobStartedAtUtc,
  finishedOrPlannedTimeAtUtc: generalTerm.jobFinishedAtUtc,
  plannedOrActualDurationSeconds: 'Czas trwania',
  isActive: 'Aktywny',
};

const MaterialCalculationModelOutput: Partial<TComponentKeys<'MaterialCalculationModel'>> = {
  displayName: 'Nazwa',
  description: 'Opis',
};

const JobStage: Partial<TComponentKeys<'JobStage'>> | Record<'state', string> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.recipeStep,
  state: 'Status',
  jobDisplayName: generalTerm.jobDisplayName,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  durationSeconds: 'Czas trwania',
  stageNo: 'NIE',
};

const JobStatus: Record<TJobStatus, string> = {
  PLANNED: 'Zaplanowane',
  STARTED: 'Rozpoczęte',
  FINISHED: 'Zakończone',
};

const OrderStatus: Record<TOrderStatus, string> = {
  CREATED: 'Utworzone',
  RELEASED: 'Zwolnione',
  STARTED: 'Rozpoczęte',
  PAUSED: 'Wstrzymane',
  FULFILLED: 'Zrealizowane',
  CANCELLED: 'Anulowane',
};

const AlarmSeverity: Record<components['schemas']['AlarmSeverity'], string> = {
  0: 'Informacje',
  1: 'Ostrzeżenie',
  2: 'Alarm',
  3: 'Nagły wypadek',
};

const AlarmEventSeverity: Record<components['schemas']['AlarmEventSeverity'], string> = {
  warning: 'Ostrzeżenie',
  alarm: 'Alarm',
  emergency: 'Nagły wypadek',
};

const HandlingState: Record<components['schemas']['HandlingState'], string> = {
  [-99]: 'Uszkodzony',
  [-10]: 'Niepowodzenie',
  [-5]: 'Niepowodzenie (oczekiwane)',
  [0]: 'Zarejestrowany',
  [10]: 'W trakcie',
  [20]: 'Zakończony',
};

const LogFileState: Partial<TComponentKeys<'LogFileState'>> = {
  filePathId: 'Ścieżka pliku',
  state: 'Stan',
  jobId: generalTerm.job,
  discoveredAtUtc: 'Wykryto dnia',
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
};

const RootCauseGroup: Partial<TComponentKeys<'RootCauseGroup'>> = {
  displayName: 'Nazwa grupy przyczyn źródłowych',
  description: generalTerm.description,
};

const EquipmentProfile: Partial<TComponentKeys<'ProfileTreeNode'>> = {
  label: 'Nazwa profilu',
  combinator: 'Typ kombinacji',
};

const TimeBucket_AssetUtilization_: Partial<TComponentKeys<'TimeBucket_AssetUtilization_'>> = {
  earliestDateUtc: 'Data rozpoczęcia',
  latestDateUtc: 'Data zakończenia',
};

const EquipmentProfileMeaning: Partial<TComponentKeys<'Meaning'>> = {
  label: 'Nazwa znaczenia',
  description: 'Opis',
  physicalDimensionId: 'Ilość fizyczna',
  preferredUnitId: 'Jednostka preferowana',
};

const EquipmentTreeNavigation: Partial<TComponentKeys<'AssetTreeNode'>> = {
  displayName: 'Nazwa',
};

const MaterialGroup: Partial<TComponentKeys<'MaterialGroup'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  materialIds: 'Materiały',
};

const MaterialCalculationModelElementCoefficient: Partial<TComponentKeys<'MaterialCalculationModelElementCoefficient'>> = {
  alloyingElementId: 'Element',
  description: generalTerm.description,
  eJcAustenite: 'eJC (Austenit)',
  eJcFerrite: 'eJC (ferryt)',
  eJnAustenite: 'eJN (Austenit)',
  eJnFerrite: 'eJN (ferryt)',
  rJjcAustenite: 'rJJC (Austenit)',
  rJjcFerrite: 'rJJC (ferryt)',
  rJjnAustenite: 'rJJN (Austenit)',
  rJjnFerrite: 'rJJN (ferryt)',
};

const MaintenanceState: Record<EMaintenanceState, string> = {
  [EMaintenanceState.PENDING]: 'Oczekujące',
  [EMaintenanceState.ACCEPTED]: 'Przyjęty',
  [EMaintenanceState.IN_PROGRESS]: 'W toku',
  [EMaintenanceState.DONE]: 'Zrobione',
  [EMaintenanceState.VALIDATED]: 'Zweryfikowano',
};

const MaintenancePriority: Record<EMaintenancePriority, string> = {
  [EMaintenancePriority.LOW]: 'Niski',
  [EMaintenancePriority.NORMAL]: 'Normalna',
  [EMaintenancePriority.HIGH]: 'Wysoki',
};

const MaintenancePeriod: Record<ECalendarPeriod, string> = {
  [ECalendarPeriod.WEEK]: 'Tydzień',
  [ECalendarPeriod.MONTH]: 'Miesiąc',
  [ECalendarPeriod.YEAR]: 'Rok',
};

const MaintenanceRecurrenceMonthDayValue: Record<EMaintenanceRecurrenceMonthDayValue, string> = {
  [EMaintenanceRecurrenceMonthDayValue.FIRST]: 'Pierwszy',
  [EMaintenanceRecurrenceMonthDayValue.SECOND]: 'Drugi',
  [EMaintenanceRecurrenceMonthDayValue.THIRD]: 'Trzeci',
  [EMaintenanceRecurrenceMonthDayValue.FOURTH]: 'Czwarty',
  [EMaintenanceRecurrenceMonthDayValue.LAST]: 'Ostatni',
};

const MaintenanceRecurrenceMonthDayDimension: Record<EMaintenanceRecurrenceMonthDayDimension, string> = {
  [EMaintenanceRecurrenceMonthDayDimension.WEEKDAY]: 'Dzień powszedni',
  [EMaintenanceRecurrenceMonthDayDimension.WEEKEND_DAY]: 'Dzień weekendowy',
  [EMaintenanceRecurrenceMonthDayDimension.DAY]: 'Dzień kalendarzowy | Dni kalendarzowe',
};

const MaintenanceRecurrenceEndCondition: Record<EMaintenanceRecurrenceEndCondition, string> = {
  [EMaintenanceRecurrenceEndCondition.AFTER]: 'Po',
  [EMaintenanceRecurrenceEndCondition.ON_THIS_DAY]: 'w tym dniu',
};

const MetadataWithChartConfigInput: Partial<TComponentKeys<'MetadataWithChartConfig'>> = {
  ...Metadata,
};

const RootCause: Partial<TComponentKeys<'RootCause'> & {
  count: string,
}> = {
  displayName: 'Przyczyna źródłowa',
  description: generalTerm.description,
  count: 'Przelicz',
};

const JobValueData: Partial<TComponentKeys<'JobValueData'>> = {
  jobId: 'Identyfikator zadania',
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Wartość',
  recipeId: 'Nr recepty',
  recipeRevision: 'Rewizja przepisu',
  recipeDisplayName: generalTerm.recipe,
};

const QualityPreset: Partial<TComponentKeys<'QualityPreset'>> = {
  displayName: generalTerm.name,
};

const Observation: Partial<TComponentKeys<'Observation'>> = {
  assetDisplayName: generalTerm.asset,
  displayName: 'Nazwa obserwacji',
  observationTimeAtUtc: 'Obserwacja miała miejsce w',
  jobFeatureDisplayName: 'Cecha pracy',
  observationType: 'Typ obserwacji',
  jobValue: 'Wartość',
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartedAtUtc: generalTerm.jobStartedAtUtc,
  jobFinishedAtUtc: generalTerm.jobFinishedAtUtc,
};

const QuenchingMedia: Partial<TComponentKeys<'QuenchingMedia'>> = {
  displayName: generalTerm.name,
  maxIntensity: 'Maksymalna intensywność',
  minIntensity: 'Minimalna intensywność',
  quenchingMediaId: 'ID',
};

const expressionFunctionCategories: Record<TFunctionCategory, string> & {
  all: string,
} = {
  all: 'Wszystko',
  arithmetic: 'Funkcje arytmetyczne',
  custom: 'Funkcje niestandardowe',
  cumulative: 'Funkcje kumulatywne',
  exponential: 'Funkcje wykładnicze',
  generative: 'Funkcje generatywne',
  imputation: 'Funkcje imputacyjne',
  logical: 'Funkcje logiczne',
  numerical: 'Funkcje numeryczne',
  operator: 'Operator',
  other: 'Inne funkcje',
  sections: 'Funkcje sekcji',
  statistical: 'Funkcje statystyczne',
  trigonometric: 'Funkcje trygonometryczne',
};

const expressionFunctionsExplanations: {
  functions: Record<TFunctionDefinition['functionType'], string>,
  operators: Record<TOperatorDefinition['operatorType'], string>,
} = {
  functions: {
    abs: 'Ta funkcja oblicza wartości bezwzględne poszczególnych elementów sygnału.',
    acos: 'Ta funkcja oblicza arcus cosinus każdego elementu sygnału.',
    add: 'Dodawanie. Dodaje dwa operandy do siebie.',
    asin: 'Ta funkcja oblicza arcus sinus każdego elementu sygnału.',
    atan2: 'Ta funkcja oblicza arcus tangens a/b dla każdej odpowiadającej pary elementów.',
    atan: 'Ta funkcja oblicza arcus tangens każdego elementu sygnału.',
    avg: 'Jeśli podano wiele argumentów, ta funkcja oblicza średnią dla każdego sygnału. W przeciwnym razie oblicza średnią ze wszystkich próbek.',
    avg_windowed: 'Ta funkcja oblicza średnią wartości sygnału dla danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    bfill: 'Wypełnij wartości NaN, używając kolejnej prawidłowej wartości, aby wypełnić lukę.',
    ceil: 'Ta funkcja zaokrągla każdy element sygnału do najbliższej liczby całkowitej.',
    clamp: 'Ta funkcja ogranicza wartości sygnału w określonym zakresie.',
    cos: 'Ta funkcja oblicza cosinus każdego elementu sygnału.',
    count_samples: 'Akumuluje liczbę próbek, w których sygnał wejściowy jest oceniany jako prawdziwy.',
    count_seconds: 'Akumuluje sekundy, w których sygnał wejściowy jest oceniany jako prawda.',
    counter: 'Ta funkcja zwiększa licznik o 1 dla każdego kroku czasowego.',
    cumulative_avg: 'Zwróć średnią skumulowaną sygnału.',
    cumulative_integrate: 'Zwraca całkę skumulowaną (używając metody trapezów w sekundach).',
    cumulative_max: 'Zwróć skumulowane maksimum sygnału.',
    cumulative_mean: 'Zwróć średnią skumulowaną sygnału.',
    cumulative_median: 'Zwróć skumulowaną medianę sygnału.',
    cumulative_min: 'Zwróć skumulowane minimum sygnału.',
    cumulative_range: 'Zwróć skumulowany zakres sygnału.',
    cumulative_std: 'Zwróć skumulowane odchylenie standardowe sygnału.',
    cumulative_sum: 'Zwraca skumulowaną sumę sygnału traktującego wartości Not a Number (NaN) jako zero. Suma skumulowana nie zmienia się po napotkaniu wartości NaN, a początkowe wartości NaN są zastępowane zerami. Zera są zwracane dla sygnałów, które są w całości wartościami NaN lub są puste',
    cumulative_var: 'Zwróć skumulowaną wariancję sygnału.',
    degrees: 'Ta funkcja konwertuje każdy element sygnału z radianów na stopnie.',
    denormalize: 'Ta funkcja denormalizuje wartości sygnału z zakresu [0, 1] do określonego zakresu.',
    diff: 'Ta funkcja oblicza różnicę między kolejnymi elementami sygnału.',
    divide: 'Dzielenie. Dzieli pierwszy operand przez drugi',
    do_nothing: 'Ta funkcja zwraca sygnał wejściowy.',
    elapsed_seconds: 'Ta funkcja zwraca liczbę sekund, które upłynęły od początku przedziału czasu.',
    equals: 'Równość. Zwraca wartość true, jeśli operandy są równe, w przeciwnym razie false.',
    exp: 'Ta funkcja oblicza wykładnik każdego elementu sygnału.',
    fill: 'Wypełnij wartości NaN w sygnale a wartościami sygnału b.',
    ffill: 'Wypełnij wartości NaN, propagując ostatnią prawidłową wartość do następnej prawidłowej.',
    floor: 'Ta funkcja zaokrągla każdy element sygnału w dół do najbliższej liczby całkowitej.',
    floor_divide: 'Dzielenie dolne. Zwraca iloraz dzielenia, odrzucając resztę.',
    gradient: 'Gradient numeryczny sygnału wejściowego jako 1/sekundę.',
    gradient_windowed: 'Gradient liczbowy w danym dx (sekundy). Ujemne lub zerowe dx spowoduje wartości NaN. Przyjmuje się, że wejściowe dx jest stałe',
    greater_than: 'Większe niż. Zwraca wartość true, jeśli pierwszy operand jest większy od drugiego.',
    greater_than_or_equal: 'Większe lub równe. Zwraca wartość true, jeśli pierwszy operand jest większy lub równy drugiemu.',
    if: 'Ta funkcja wykonuje operację warunkową na poziomie elementu na podstawie warunku',
    integrate: 'Ta funkcja integruje sygnał wejściowy, wykorzystując złożoną regułę trapezową w ciągu sekund.',
    interpfill: 'Wypełnij wartości NaN poprzez liniową interpolację pomiędzy ostatnią i następną prawidłową wartością.',
    is_finite: 'Ta funkcja sprawdza, czy elementy sygnału są skończone pod względem elementów.',
    less_than: 'Mniejsze niż. Zwraca wartość true, jeśli pierwszy operand jest mniejszy od drugiego.',
    less_than_or_equal: 'Mniejsze lub równe. Zwraca wartość true, jeśli pierwszy operand jest mniejszy lub równy drugiemu.',
    ln: 'Ta funkcja oblicza logarytm naturalny każdego elementu sygnału.',
    log10: 'Ta funkcja oblicza logarytm dziesiętny każdego elementu sygnału.',
    log2: 'Ta funkcja oblicza logarytm o podstawie 2 każdego elementu sygnału.',
    log: 'Ta funkcja oblicza logarytm każdego elementu sygnału o podstawie b.',
    logical_and: 'Logiczne AND. Zwraca wartość true, jeśli oba operandy są prawdziwe, w przeciwnym razie false.',
    logical_not: 'Jednoargumentowe logiczne NIE. Zwraca wartość true, jeśli operand jest fałszywy, i false, jeśli operand jest prawdziwy.',
    logical_or: 'Logiczne LUB. Zwraca wartość true, jeśli przynajmniej jeden z operandów jest prawdą, w przeciwnym razie false.',
    logical_xor: 'Logiczny XOR. Zwraca fałsz, jeśli oba operandy mają ten sam stan logiczny, w przeciwnym razie prawdę.',
    max: 'Jeśli podano wiele argumentów, ta funkcja znajduje maksymalną wartość wśród sygnałów. W przeciwnym razie znajduje maksymalną wartość wśród wszystkich próbek',
    max_windowed: 'Ta funkcja oblicza maksymalną wartość sygnału w danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    mean: 'Jeśli podano wiele argumentów, ta funkcja oblicza średnią dla każdego sygnału. W przeciwnym razie oblicza średnią ze wszystkich próbek.',
    mean_windowed: 'Ta funkcja oblicza średnią wartości sygnału dla danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    median: 'Jeśli podano wiele argumentów, ta funkcja znajduje wartość mediany wśród sygnałów. W przeciwnym razie znajduje wartość mediany wśród wszystkich próbek.',
    median_windowed: 'Ta funkcja oblicza medianę wartości sygnału dla danej długości okna w sekundach. Ujemna długość okna zostanie przekształcona na długość okna równą 0, co daje wartość NaN',
    min: 'Jeśli podano wiele argumentów, ta funkcja znajduje maksymalną wartość wśród sygnałów. W przeciwnym razie znajduje maksymalną wartość wśród wszystkich próbek',
    min_windowed: 'Ta funkcja oblicza minimalną wartość sygnału dla danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    modulo: 'Moduł. Zwraca resztę z dzielenia pierwszego operandu przez drugi.',
    multiply: 'Mnożenie. Mnoży dwa operandy przez siebie.',
    negate: 'Ta funkcja neguje każdy element sygnału.',
    normalize: 'Ta funkcja normalizuje wartości sygnału do zakresu [0, 1]',
    not_equals: 'Nierówność. Zwraca wartość true, jeśli operandy nie są równe, w przeciwnym razie false.',
    percentile: 'Ta funkcja oblicza percentyl wartości sygnału.',
    power: 'Potęgowanie. Podnosi pierwszy operand do potęgi drugiego operandu.',
    radians: 'Ta funkcja konwertuje każdy element sygnału ze stopni na radiany.',
    ramp: 'Ta funkcja zwraca liniową rampę od 0 do 1.',
    random: 'Ta funkcja zwraca liczbę losową z zakresu od 0 do 1 dla każdego kroku czasowego.',
    range: 'Jeśli podano wiele argumentów, ta funkcja oblicza zakres (maks. - min.) dla każdego sygnału. W przeciwnym razie oblicza zakres dla wszystkich próbek',
    range_windowed: 'Ta funkcja oblicza zakres wartości sygnału w danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    relative_error: 'Zwraca błąd względny `((rzeczywisty / względny) - 1) * 100`, gdzie względny jest != 0, a wartości są skończone.',
    round: 'Ta funkcja zaokrągla każdy element sygnału do najbliższej liczby całkowitej.',
    sections_join: 'Usuwa wszystkie przerwy między sekcjami, które są krótsze niż określony próg, poprzez połączenie sekcji obejmujących.',
    sections_first: 'Zwraca pierwszą dostępną sekcję. Może nie zwrócić żadnej sekcji, jeśli żadna nie istnieje.',
    sections_last: 'Zwraca ostatnią dostępną sekcję. Może nie zwrócić żadnej sekcji, jeśli żadna nie istnieje.',
    sections_nth: 'Zwraca najkrótszą(e) sekcję(e). Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji o minimalnym czasie trwania.',
    sections_shortest: 'Zwraca najkrótszą sekcję(e). Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji o minimalnej długości.',
    sections_median_duration: 'Zwraca sekcje, których czas trwania jest równy medianie czasu trwania wszystkich wykrytych sekcji.',
    sections_longest: 'Zwraca najdłuższą(e) sekcję(e). Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji o maksymalnym czasie trwania.',
    sections_max_value: 'Zwraca sekcje zawierające największą wartość całkowitą. Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji zawierających maksymalną wartość',
    sections_median_value: 'Zwraca sekcje zawierające wartość mediany. Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji zawierających medianę',
    sections_min_value: 'Zwraca sekcje zawierające najmniejszą wartość. Może zwrócić wiele sekcji, jeśli istnieje wiele sekcji zawierających minimalną wartość',
    sign: 'Ta funkcja zwraca znak każdego elementu sygnału.',
    sin: 'Ta funkcja oblicza sinus każdego elementu sygnału.',
    sqrt: 'Ta funkcja oblicza pierwiastek kwadratowy każdego elementu sygnału.',
    stage_number: 'Zwraca numer kroku przepisu w danym momencie, jeśli zadanie jest aktywne. W przeciwnym razie zwraca NaN',
    std: 'Jeśli podano wiele argumentów, ta funkcja oblicza odchylenie standardowe dla każdego sygnału. W przeciwnym razie oblicza odchylenie standardowe dla wszystkich próbek.',
    std_windowed: 'Ta funkcja oblicza odchylenie standardowe wartości sygnału dla danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    steady_state_detection: 'Ta funkcja pozwala znaleźć obszary o niemal stałych wartościach. Długość okna pozwala na wygładzenie sygnału wejściowego, a wartość graniczna pozwala na zdefiniowanie maksymalnego gradientu, gdy sygnał nadal jest uznawany za stały.',
    subtract: 'Odejmowanie. Odejmuje drugi operand od pierwszego.',
    sum: 'Jeśli podano wiele argumentów, ta funkcja oblicza sumę dla każdego sygnału. W przeciwnym razie oblicza sumę wszystkich próbek.',
    sum_windowed: 'Ta funkcja oblicza sumę wartości sygnału w danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN.',
    tan: 'Ta funkcja oblicza tangens każdego elementu sygnału.',
    timestamp: 'Ta funkcja zwraca milisekundy od epoki (1970-01-01 00',
    total_seconds: 'Zwraca całkowitą liczbę sekund aktualnie widocznego przedziału czasu jako stałą.',
    trunc: 'Ta funkcja obcina każdy element sygnału w kierunku zera.',
    var: 'Jeśli podano wiele argumentów, ta funkcja oblicza wariancję dla każdego sygnału. W przeciwnym razie oblicza wariancję dla wszystkich próbek.',
    var_windowed: 'Ta funkcja oblicza zmienną wartości sygnału w danej długości okna w sekundach. Ujemna długość okna zostanie przekonwertowana na długość okna równą 0, co daje wartość NaN',
    first: 'Ta funkcja zwraca stałą pierwszej próbki.',
    last: 'Ta funkcja zwraca stałą ostatniej próbki.',
    shift: 'Przesuwa sygnał o zadaną liczbę próbek. Wartości dodatnie przesuną się w prawo, a ujemne w lewo. Nowe wartości są wypełniane wartościami NaN',
  },
  operators: {
    '!': 'Jednoargumentowe logiczne NIE. Zwraca wartość true, jeśli operand jest fałszywy, i false, jeśli operand jest prawdziwy.',
    '-': 'Odejmowanie. Odejmuje drugi operand od pierwszego.',
    '+': 'Dodawanie. Dodaje dwa operandy do siebie.',
    '**': 'Potęgowanie. Podnosi pierwszy operand do potęgi drugiego operandu.',
    '*': 'Mnożenie. Mnoży dwa operandy przez siebie.',
    '//': 'Dzielenie dolne. Zwraca iloraz dzielenia, odrzucając resztę.',
    '/': 'Dzielenie. Dzieli pierwszy operand przez drugi.',
    '%': 'Moduł. Zwraca resztę z dzielenia pierwszego operandu przez drugi.',
    '==': 'Równość. Zwraca wartość true, jeśli operandy są równe, w przeciwnym razie false.',
    '!=': 'Nierówność. Zwraca wartość true, jeśli operandy nie są równe, w przeciwnym razie false.',
    '<': 'Mniejsze niż. Zwraca wartość true, jeśli pierwszy operand jest mniejszy od drugiego.',
    '<=': 'Mniejsze lub równe. Zwraca wartość true, jeśli pierwszy operand jest mniejszy lub równy drugiemu.',
    '>': 'Większe niż. Zwraca wartość true, jeśli pierwszy operand jest większy od drugiego.',
    '>=': 'Większe lub równe. Zwraca wartość true, jeśli pierwszy operand jest większy lub równy drugiemu.',
    '&&': 'Logiczne AND. Zwraca wartość true, jeśli oba operandy są prawdziwe, w przeciwnym razie false.',
    '||': 'Logiczne LUB. Zwraca wartość true, jeśli przynajmniej jeden z operandów jest prawdą, w przeciwnym razie false.',
    '^': 'Logiczne XOR. Zwraca fałsz, jeśli oba operandy mają ten sam stan logiczny, w przeciwnym razie prawdę.',
  },
};

const DataMigrationState: TComponentKeys<'DataMigrationState'> = {
  assetId: generalTerm.assetId,
  displayName: generalTerm.displayName,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Najnowsze dane',
  lastEtlRunAtUtc: 'Ostatnia migracja',
  etlLagSeconds: 'Opóźnienie migracji',
  dataId: 'Identyfikator',
  dataFormat: 'Typ',
  dataSourceReference: generalTerm.dataSourceReference,
};

const JobMigrationState: TComponentKeys<'JobMigrationState'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Najnowsze dane',
  lastEtlRunAtUtc: 'Ostatnia migracja',
  etlLagSeconds: 'Opóźnienie migracji',
};

const QmulusDriver = {
  PT9800: {
    DataMigrationState,
    JobMigrationState,
  },
};

const TableTestData: Partial<TComponentKeys<'TableTestData'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Wartość',
  recipeDisplayName: generalTerm.recipe,
  partDisplayName: generalTerm.part,
  sampleDisplayName: 'Próbka',
};

const ModelBuilderModelOverview: Partial<TComponentKeys<'ModelBuilderModelOverview'>> = {
  accuracy: 'Dokładność',
  assetId: generalTerm.asset,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: 'Autor',
  displayName: 'Model',
  inputDisplayNames: 'Sygnały wejściowe',
  lifecycleState: 'Stan',
  modelRevision: 'Wersja',
  modelType: 'Typ modelu',
  outputDisplayName: 'Sygnał wyjściowy',
  revisionCount: 'Wersje',
};

const ModelLifecycleState: Record<EModelLifecycleState, string> = {
  [EModelLifecycleState.REGISTERED]: 'Zarejestrowany',
  [EModelLifecycleState.LOADING_DATA]: 'Ładowanie danych treningowych',
  [EModelLifecycleState.PREPARING_DATA]: 'Przetwarzanie danych szkoleniowych',
  [EModelLifecycleState.TRAINING]: 'Szkolenie w toku',
  [EModelLifecycleState.SAVING_MODEL]: 'Model zapisu',
  [EModelLifecycleState.CREATING_METADATA]: 'Rejestrowanie sygnałów',
  [EModelLifecycleState.READY]: 'Gotowy',
};

const ModelBuilderModelType: Record<components['schemas']['ModelBuilderModelType'], string> = {
  LINEAR_REGRESSION: 'Regresja liniowa',
  SMART_REGRESSION: 'Inteligentna regresja',
};

const ModelBuilderTrainingRequest: Partial<TComponentKeys<'ModelBuilderTrainingRequest'>> = {
  displayName: 'Nazwa modelu',
  testSize: 'Podział testowy',
};

const TimeBucketType: Record<TComponentValue<'TimeBucketType'>, string> = {
  day: 'Dni',
  week: 'Tygodnie',
  month: 'Miesiące',
  quarter: 'Kwartały',
  year: 'Lata',
  full: 'Cały przedział czasowy',
  auto: 'Automatyczny',
};

const AlarmStatistics: Partial<TComponentKeys<'AlarmStatistics'>> = {
  displayName: generalTerm.displayName,
  severity: AlarmDefinition.severity,
};

const AssetBlobIndex: Partial<TComponentKeys<'AssetBlobIndex'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  semanticMeaningId: 'Kategoria',
  ownerDisplayName: 'Przesłane przez',
};

const BlobMeaning: Record<TComponentValue<'BlobMeaning'>, string> = {
  [EBlobMeaning.MANUAL]: 'Podręcznik',
  [EBlobMeaning.DRAWING]: 'Rysunek',
  [EBlobMeaning.CONFIGURATION]: 'Konfiguracja',
  [EBlobMeaning.BILL_OF_MATERIALS]: 'Zestawienie materiałów',
  [EBlobMeaning.TENANT_LOGO]: 'Logo',
  [EBlobMeaning.PART_IMAGE]: 'Obraz części',
};

const Meaning: Partial<TComponentKeys<'Meaning'>> = {
  meaningId: 'Oznaczający',
  userDefinedDisplayName: 'Nadpisanie nazwy domyślnej',
  userDefinedDescription: generalTerm.description,
  physicalDimensionId: 'Wymiar fizyczny',
  preferredUnitId: 'Jednostka preferowana',
  defaultColor: generalTerm.color,
};

const MaintenanceEventOverview: TComponentKeys<'MaintenanceEventOverview'> = {
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  assetDisplayName: generalTerm.asset,
  assetId: generalTerm.asset,
  assetLocation: generalTerm.location,
  assigneeDisplayName: 'Assignee',
  assigneeId: 'Assignee',
  calendarEventType: 'Event type',
  color: generalTerm.color,
  displayName: generalTerm.displayName,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  maintenanceSeriesId: 'Series',
  occurrenceId: 'Occurrence',
  priority: 'Priority',
  startAtUtc: generalTerm.jobStartedAtUtc,
  state: 'State',
};

const MaintenanceEventDetails: Partial<TComponentKeys<'MaintenanceEventDetails'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  seriesRecurrence: 'Nawrót',
  seriesAttachments: 'Załączniki',
  overheadSeconds: 'Narzut czasu trwania zadania',
  taskInstances: 'Zadania konserwacyjne',
  assetBlobs: 'Dokumentacja zasobów',
};

const MaintenanceSeriesCreate: Partial<TComponentKeys<'MaintenanceSeriesCreate'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  recurrence: MaintenanceEventDetails.seriesRecurrence,
  overheadSeconds: MaintenanceEventDetails.overheadSeconds,
  taskTemplates: MaintenanceEventDetails.taskInstances,
  attachments: MaintenanceEventDetails.seriesAttachments,
};

const CalendarEventTag: TComponentKeys<'CalendarEventTag'> = {
  calendarEventTagId: 'Id',
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  color: generalTerm.color,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: generalTerm.createdBy,
  createdByUserId: generalTerm.createdBy,
  deletedAtUtc: generalTerm.deletedAt,
  deletedBy: generalTerm.deletedBy,
  deletedByDisplayName: generalTerm.deletedBy,
  description: generalTerm.description,
  displayName: generalTerm.displayName,
  isDeleted: generalTerm.deleted,
};

const OperationEventOverview: TComponentKeys<'OperationEventOverview'> = {
  assetLocation: generalTerm.location,
  calendarEventId: 'id',
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  startAtUtc: generalTerm.jobStartedAtUtc,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  assetId: generalTerm.asset,
  assetDisplayName: generalTerm.asset,
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  calendarEventTag: 'Event tag',
};

const OperationEventDetails: TComponentKeys<'OperationEventDetails'> = {
  ...OperationEventOverview,
  description: generalTerm.description,
};

const MaintenanceTaskResultType: Record<TComponentValue<'MaintenanceTaskResultType'>, string> = {
  WORK_DONE: 'Zrobione',
  OMITTED: 'Pominięto',
};

const MaintenanceTaskReviewType: Record<TComponentValue<'MaintenanceTaskReviewType'>, string> = {
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
};

const TaskInstance: Partial<TComponentKeys<'TaskInstance'>> = {
  description: generalTerm.description,
  durationSeconds: 'Czas trwania',
  attachments: 'Załączniki',
  workResult: 'Stan',
  reviewResult: 'Recenzja',
};

const Role: Partial<TComponentKeys<'Role'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  isDefault: 'domyślna rola',
};

const RoleWithPermissions: Partial<TComponentKeys<'RoleWithPermissions'>> = {
  ...Role,
};

const SimulatorAMS275910CompoundLayerClass: Record<ESimulatorAMS275910CompoundLayerClass, string> = {
  [ESimulatorAMS275910CompoundLayerClass.CLASS_0]: 'Brak warstwy kompozytowej',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_1]: '0 - 13 µm',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_2]: '13 - 25 µm',
};

const SimulatorAMS275910SteelClass: Record<ESimulatorAMS275910SteelClass, string> = {
  [ESimulatorAMS275910SteelClass.NITRALLOY_135M]: 'Nitralloy 135M',
  [ESimulatorAMS275910SteelClass.NITRALLOY_EZ]: 'Nitralloy EZ',
  [ESimulatorAMS275910SteelClass.NITRALLOY_N]: 'Nitralloy N',
  [ESimulatorAMS275910SteelClass._4140_4340]: '4140 / 4340',
  [ESimulatorAMS275910SteelClass.DC_D6AC]: 'DC / D6AC',
  [ESimulatorAMS275910SteelClass.H11]: 'H11',
  [ESimulatorAMS275910SteelClass.STAINLESS]: 'Nierdzewny',
  [ESimulatorAMS275910SteelClass.CARBON]: 'Węgiel',
};

const SimulatorAMS275912PorosityClass: Record<ESimulatorAMS275912PorosityClass, string> = {
  [ESimulatorAMS275912PorosityClass.CLASS_1]: 'Klasa 1',
  [ESimulatorAMS275912PorosityClass.CLASS_2]: 'Klasa 2',
};

const SimulatorAMS275912SteelClass: Record<ESimulatorAMS275912SteelClass, string> = {
  [ESimulatorAMS275912SteelClass.GROUP_0]: 'Grupa 0',
  [ESimulatorAMS275912SteelClass.GROUP_1]: 'Grupa 1',
  [ESimulatorAMS275912SteelClass.GROUP_2]: 'Grupa 2',
};

const SimulatorPreTreatmentType: Record<ESimulatorPreTreatmentType, string> = {
  [ESimulatorPreTreatmentType.NORMALIZED]: 'Znormalizowany',
  [ESimulatorPreTreatmentType.QUENCHED]: 'Ugaszone',
  [ESimulatorPreTreatmentType.TEMPERED]: 'Zahartowany',
};

const SimulatorSimulationPart: Partial<TComponentKeys<'SimulatorSimulationPart'>> = {
  setpointMinSurfaceHardness: 'Min. twardość powierzchni',
  setpointCaseDepth: 'Efektywna głębokość przypadku',
  setpointCaseDepthHardness: generalTerm.requiredHardness,
  grainSize: generalTerm.grainSize,
  doesUseShape: 'Typ symulacji kształtu',
  shape: 'Ogólny kształt części (DIN 17211)',
  quenchingMediaId: 'Media gaśnicze',
  quenchingIntensity: generalTerm.quenchingIntensity,
  endTemperature: generalTerm.endTemperature,
  nitridingMinSurfaceHardness: 'Min. twardość powierzchni',
  nitridingHardnessDepth: generalTerm.nitridingDepth,
  nitridingHardness: 'Twardość azotowana',
  nitridingSteelClass: 'Grupa stalowa (AMS 2759-10)',
  nitridingCompoundLayerClass: 'Klasa Compoundlayer (AMS 2759-10)',
  nitrocarburizingPorosityClass: 'Porowatość (AMS 2759-12)',
  nitrocarburizingSteelClass: 'Grupa stalowa (AMS 2759-12)',
};

const AssetStatusJobInfo: Partial<TComponentKeys<'AssetStatusJobInfo'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  treatmentSpecificationDisplayName: generalTerm.recipe,
  stageDisplayName: generalTerm.recipeStep,
  stageNo: 'Stage number',
};

const AssetStatusOverview: Partial<TComponentKeys<'AssetStatusOverview'>> = {
  activeAlarmCount: 'Aktywne alarmy',
  state: 'Status',
};

const AssetState: Record<TComponentValue<'AssetState'>, string> = {
  RUNNING: 'Działanie',
  OFFLINE: 'Wyłączony',
  IDLE: 'Bezczynny',
  PLANNED_UNIT_DOWNTIME: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  ACTUAL_UNIT_DOWNTIME: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  OPERATION_HOLD: CalendarEventType.OPERATION_HOLD,
};

const QmulusLicenseViolationReason: Record<TComponentValue<'QmulusLicenseViolationReason'>, string> = {
  NO_LICENSE_KEY: 'Brak licencji',
  ASSET_LIMIT_VIOLATED: 'Skonfigurowano zbyt wiele zasobów',
  EXPIRED: 'Licencja wygasła',
  EXPIRED_GRACE_PERIOD: 'Licencja wygasła',
  INVALID: 'Nieprawidłowa licencja',
  USER_LIMIT_VIOLATED: 'Utworzono zbyt wielu użytkowników',
  OTHER: 'Naruszenie licencji',
};

const AuditEnvelope: Partial<TComponentKeys<'AuditEnvelope'>> = {
  valueSensorMeanings: 'Czujniki',
  durationMeanings: 'Czujniki czasowe',
  stabilizationCombinationType: 'Stabilizacja',
  stabilizationStagesCnt: 'Kroki receptury',
  stabilizationPeriodSeconds: 'Czas trwania',
};

const EnvelopeThreshold: TComponentKeys<'EnvelopeThreshold_int_'> = {
  lowerOffset: 'Limit -',
  setpoint: 'Punkt nastawy',
  upperOffset: 'Limit +',
};

const AuditEnvelopStabilizationCombinationType: Record<TComponentValue<'AuditEnvelopStabilizationCombinationType'>, string> = {
  0: 'Dodaj',
  1: 'Najkrótszy',
  2: 'Najdłuższy',
};

const ScaleType: Record<TComponentValue<'ScaleType'>, string> = {
  linear: 'Liniowy',
  logarithmic: 'Logarytmiczny',
};

const ChemicalElementInSteel: Record<TComponentValue<'ChemicalElementInSteel'>, string> = {
  Fe: 'Żelazo',
  C: 'Węgiel',
  Mn: 'Mangan',
  P: 'Fosfor',
  S: 'Siarka',
  Si: 'Krzem',
  Cr: 'Chrom',
  V: 'Wanad',
  W: 'Wolfram',
  Mo: 'Molibden',
  Co: 'Kobalt',
  Ni: 'Nikiel',
  Cu: 'Miedź',
  Al: 'Aluminium',
  Ti: 'Tytan',
  Nb: 'Niob',
  B: 'Bor',
  Pb: 'Ołów',
  Zr: 'Cyrkon',
  Ta: 'Tantal',
  N: 'Azot',
  Se: 'Selen',
};

const EquipmentPhysicalType: Record<TComponentValue<'EquipmentPhysicalType'>, string> = {
  1: 'Piec szybowy',
  2: 'Piec retortowy dołkowy',
  10: 'Piec dzwonowy',
  20: 'Piec poziomy',
  21: 'Piec retortowy poziomy',
  30: 'Piec skrzynkowy',
  40: 'Piec wielokomorowy',
  50: 'Piec obrotowy',
  51: 'Piec przepychający',
  52: 'Piec pierścieniowy',
  100: 'Piec taśmowy',
  110: 'Piec retortowy obrotowy',
  120: 'Piec Shaker Hearth',
  200: 'Magazyn',
  201: 'Miejsce załadunku',
  202: 'Miejsce rozładunku',
  203: 'Miejsce załadunku/rozładunku',
};
const DataSourceType: Record<TComponentValue<'DataSourceType'>, string> = {
  ...NonTranslatable.dataSourceName,
  production_statistics: AppId[EAppId.PRODUCTION_STATISTICS],
  model_builder: AppId[EAppId.MODEL_BUILDER],
  ml_model: `${AppId[EAppId.MODEL_BUILDER]} (Legacy)`,
  generic: 'Ogólne źródło danych / Node-RED',
};

const NpmSystemIdentifier: TComponentKeys<'NpmSystemIdentifier'> = {
  customerCode: 'kodklienta',
  location: 'lokalizacja',
  systemId: 'identyfikator systemu',
  subSystemId: 'podsystemId',
};

const PtDatabaseIdentifier: TComponentKeys<'PtDatabaseIdentifier'> = {
  type: 'Typ',
  ptDbId: 'Numer PE',
};

const WorkplaceIdentifier: TComponentKeys<'WorkplaceIdentifier'> = {
  ...PtDatabaseIdentifier,
  workplaceId: 'workplaceId',
  chamberId: 'chamberId',
};

const AutomatIdentifier: TComponentKeys<'AutomatIdentifier'> = {
  ...WorkplaceIdentifier,
  automatNo: 'automatNo',
};

const AssetParameter: Partial<TComponentKeys<'AssetParameter'>> = {
  namespace: 'Przestrzeń nazw',
  category: 'Kategoria',
  parameterName: 'Parametr',
  sourceAssetId: 'Źródło',
  value: 'Wartość',
};

const AssetParameterDefinition: Partial<TComponentKeys<'AssetParameterDefinition'>> = {
  namespace: AssetParameter.namespace,
  category: AssetParameter.category,
  parameterName: AssetParameter.parameterName,
  physicalDimensionId: Meaning.physicalDimensionId,
  description: generalTerm.description,
  defaultValue: 'Wartość domyślna',
  allowedMin: 'Wartość minimalna',
  allowedMax: 'Maksymalna wartość',
};

const ExpressionType: Record<Exclude<TValidateExpressionRouteResponse['expression']['type'], undefined> | typeof sinkExpression | typeof sinkVariable, string> = {
  ExpressionConstant: 'Stały',
  ExpressionOperator: 'Operator',
  ExpressionUnaryOperator: 'Operator',
  ExpressionDataIdentifier: 'Sygnał',
  ExpressionMeaningIdentifier: 'Oznaczający',
  ExpressionFunction: 'ExpressionFunction',
  ExpressionGenerativeFunction: 'Funkcje generatywne',
  ExpressionVariableIdentifier: 'Zmienny',
  ExpressionAssetParameterIdentifier: 'Parametr zasobu',
  SinkExpression: 'Wyrażenie',
  SinkVariable: 'Definicja zmiennej',
};

const ExpressionNamedConstant: Record<TComponentValue<'ExpressionNamedConstant'>, string> = {
  pi: 'Pi π',
  e: 'Liczba Eulera',
  inf: 'Nieskończoność',
  nan: 'To nie jest liczba',
  true: 'PRAWDA',
  false: 'FAŁSZ',
  null: 'nieważny',
  millisecond: 'Milisekunda',
  minute: 'Minuta',
  hour: 'Godzina',
};

const ExpressionOperatorType: Record<TComponentValue<'ExpressionOperatorType'>, string> = {
  '**': 'potęgowanie',
  '*': 'mnożenie',
  '//': 'podział piętra',
  '/': 'dział',
  '%': 'modulo',
  '+': 'dodatek',
  '-': 'odejmowanie',
  '==': 'równy',
  '!=': 'nie równy',
  '<': 'mniej niż',
  '<=': 'mniejsze lub równe',
  '>': 'większy niż',
  '>=': 'większy lub równy',
  '^': 'bitowe XOR',
  '&&': 'logiczne AND',
  '||': 'logiczne LUB',
};

const ExpressionUnaryOperatorType: Record<TComponentValue<'ExpressionUnaryOperatorType'>, string> = {
  '!': 'logiczne NIE',
  '-': 'negacja',
  '+': 'identyfikator',
};

const ExpressionFunctionTypeAggregation: Record<TComponentValue<'ExpressionFunctionTypeAggregation'>, string> = {
  min: 'Minimum',
  max: 'Maksymalny',
  range: 'Zakres',
  sum: 'Suma',
  mean: 'Mieć na myśli',
  avg: 'Przeciętny',
  std: 'Odchylenie standardowe',
  var: 'Rozbieżność',
  median: 'Mediana',
};

const QmulusDashboardWidget: Record<keyof TQmulusDashboardWidgetSharedProps, string> = {
  displayName: generalTerm.displayName,
  widgetType: 'Typ',
  size: 'Rozmiar',
};

const QmulusDashboardWidgetType: Record<TQmulusDashboardWidgetType, string> = {
  anomaly_detection_observations: `${AppId[EAppId.ANOMALY_DETECTION]}: Obserwacje`,
  camera: 'Kamera',
  gauge: 'Miernik',
  job_features: DataFormat.job_aggregate,
  timeseries: DataFormat.time_series,
};

const QmulusDashboardWidgetObservations: Pick<TComponentKeys<'QmulusDashboardWidgetObservations'>, 'variant'> = {
  variant: 'Wariant',
};

const QmulusDashboardWidgetObservationsVariant: Record<components['schemas']['QmulusDashboardWidgetObservations']['variant'], string> = {
  details: 'Szczegóły',
  frequency: 'Częstotliwość',
};

const QmulusDashboardMixinData: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'assetId' | 'dataFormat' | 'data' | 'lookback'> = {
  assetId: generalTerm.assetId,
  dataFormat: Metadata.dataFormat!,
  data: 'Dane',
  lookback: 'Zakres czasu',
};

const QmulusDashboardMixinLimits: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'limitMin' | 'limitMax'> = {
  limitMin: 'Minimum',
  limitMax: 'Maksymalny',
};

const QmulusDashboardWidgetGauge: Partial<TComponentKeys<'QmulusDashboardWidgetGauge'>> = {
  ...QmulusDashboardWidget,
  ...QmulusDashboardMixinData,
  ...QmulusDashboardMixinLimits,
  appearance: 'Wygląd',
  thresholds: 'Progi',
};

const QmulusDashboardWidgetGaugeAppearance: Record<TComponentValue<'QmulusDashboardWidgetGauge'>['appearance'], string> = {
  180: 'Łuk - 180°',
  270: 'Łuk - 270°',
  horizontal: 'Liniowo – Poziomo',
  vertical: 'Liniowy – Pionowy',
};

const QmulusDashboardNamedLimit: Record<TComponentValue<'QmulusDashboardNamedLimit'>, string> = {
  standard_deviation: 'Średnia + b * odchylenie standardowe',
  lower_whisker: 'Q1 - 1,5 * IQR',
  upper_whisker: 'Q3 + 1,5 * IQR',
  q1: 'Kwartyl 1',
  q3: 'Kwartyl 3',
};

const QmulusDashboardThreshold: TComponentKeys<'QmulusDashboardThreshold'> = {
  value: 'Wartość',
  color: generalTerm.color,
};

const QmulusDashboard: Partial<TComponentKeys<'QmulusDashboard'>> = {
  displayName: generalTerm.displayName,
};

const SimulatorRecipeSetpointType: Record<TComponentValue<'SimulatorRecipeSetpoint'>['simulatorSetpointMeaningId'], string> = {
  RUN_TIME: 'Środowisko wykonawcze',
  TEMPERATURE: 'Temperatura',
  CARBURIZING_DEPTH_RELATIVE: 'Głębokość nawęglania (względna)',
  CARBURIZING_DEPTH_ABSOLUTE: 'Głębokość nawęglania (absolutna)',
  CARBON_POTENTIAL_CP: 'Potencjał węglowy CP',
  SURFACE_CARBON: PartMaterialProperties.surfaceCarbon,
  AUTO_BOOST_C: 'Auto C (100-x)',
  SOOT_LIMIT: 'Granica sadzy',
  NITROGEN_POTENTIAL_NP: 'Potencjał azotowy NP',
  NITRIDING_DEPTH_RELATIVE: 'Głębokość azotowania (względna)',
  NITRIDING_DEPTH_ABSOLUTE: 'Głębokość azotowania (absolutna)',
  NITRIDING_POTENTIAL_KN: 'Potencjał azotowania kN',
  SURFACE_NITROGEN: 'Azot powierzchniowy',
  AUTO_BOOST_N: 'Auto N (100-x)',
  CARBURIZING_POTENTIAL_KC_B: 'Potencjał nawęglania kC (Bodouard)',
  CARBURIZING_POTENTIAL_KC_W: 'Potencjał nawęglania kC(H₂O)',
  TOTAL_GAS_FLOW: 'Przepływ objętościowy (stały)',
  TOTAL_GAS_FLOW_AUTO: 'Przepływ objętościowy (auto-puls)',
  CO_CONTENT: generalTerm.carbonMonoxide,
  H2_CONTENT: generalTerm.hydrogen,
  N2_CONTENT: 'Azot N₂',
  NH3_CONTENT: 'Amoniak NH₃',
  COX_CONTENT: 'COₓ',
  C2H2_CONTENT: 'Acetylen C₂H₂',
  C3H8_CONTENT: 'Propan C₃H₈',
  WAIT_RELATIVE: 'Czekaj',
};

const SimulatorRecipeStep: TComponentKeys<'SimulatorRecipeStepMutation'> = {
  stageId: generalTerm.recipeStage,
  displayName: generalTerm.displayName,
  setpoints: generalTerm.setpoint,
};

const SimulatorRecipeSetpoint: TComponentKeys<'SimulatorRecipeSetpoint'> = {
  simulatorSetpointMeaningId: generalTerm.setpoint,
  unitOfMeasurementId: generalTerm.unit,
  control: 'Sterowanie wartością zadaną',
  endControl: 'Warunek końcowy',
};

const PT9800RecipeSimulationInput: TComponentKeys<'PT9800RecipeSimulationInput'> = {
  recipe: generalTerm.recipe,
  recipeMetadata: '',
  intervalSeconds: '',
  dataSourceId: generalTerm.dataSourceId,
  startTemperature: 'Temperatura początkowa',
  heatingGradient: 'Gradient ogrzewania',
  coolingGradient: 'Gradient chłodzenia',
  assetFactor: 'Współczynnik pieca',
  coContent: 'CO',
  h2Content: 'H2',
};

const CCTPhaseBoundaryType: Record<TComponentValue<'CCTBoundaryType'>, string> = {
  PEARLITE: 'Perlit',
  FERRITE: 'Ferryt',
  BAINITE: 'Bainit',
  CARBIDE: 'Węglik',
};

const JobConsumption: TComponentKeys<'JobConsumption'> = {
  dataId: 'Materiał eksploatacyjny',
  meaningId: 'Znaczenie',
  value: 'Ilość',
  unitOfMeasureId: generalTerm.unit,
};

const JobValueComparison: TComponentKeys<'JobValueComparison'> = {
  dataId: DataFormat.job_aggregate,
  jobId: generalTerm.job,
  value: 'Wartość',
  comparison: 'Porównanie',
};

const JobValueComparisonPeriod: TComponentKeys<'JobValueComparisonPeriod'> = {
  previous30D: 'Porównanie do poprzednich 30 dni',
  previous90D: 'Porównanie do poprzednich 90 dni',
  previousYear: 'Porównanie do poprzedniego roku',
};

const JobValueBaselineMetrics: TComponentKeys<'JobValueBaselineMetrics'> = {
  isOutlier: 'Wartość odstająca statystycznie',
  delta: 'Zmiana bezwzględna',
  deltaPercent: 'Zmiana względna',
  standardDeviation: 'Odchylenie standardowe',
  baseline: 'Średnia z okresu',
  earliestDateUtc: 'Początek okresu',
  latestDateUtc: 'Koniec okresu',
};

const AuditTrailSettings: TComponentKeys<'AuditTrailSettings'> = {
  isEnabled: 'Włączone',
  retentionPeriodDays: 'Okres przechowywania',
};

const AuditTrailEvent: TComponentKeys<'AuditTrailEvent'> = {
  timestampUtc: 'Znacznik czasu',
  userId: 'Użytkownik',
  userDisplayName: 'Użytkownik',
  endpointMethod: 'Metoda',
  endpoint: 'Endpoint',
  params: 'Parametry',
  appKey: 'API',
  returnStatus: 'Status',
};

const AuditSpecificationResultDetail: Partial<TComponentKeys<'AuditSpecificationResultDetail'>> = {
  auditorRemark: 'Uwagi audytora',
};

const Order: TComponentKeys<'Order'> = {
  orderId: 'Zamówienie',
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  customerDisplayName: Customer.displayName,
  customerId: Customer.customerId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  partDisplayName: Part.displayName,
  partId: Part.partId,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  partRevisionId: 'Rewizja części',
  attributes: 'Atrybuty zamówienia',
  status: 'Status',
  target: 'Cel',
};

const OperationOrder: Partial<TComponentKeys<'OperationOrder'>> = {
  displayName: Order.orderId,
  customerId: generalTerm.customer,
};

const PartInstance: TComponentKeys<'PartInstance'> = {
  partInstanceId: Part.displayName,
  displayName: Part.displayName,
  partId: Part.partId,
  jobId: Job.jobId,
  jobDisplayName: Job.displayName,
  orderId: Order.orderId,
  orderDisplayName: Order.orderId,
  partCount: 'Ilość',
  partRevisionId: Part.partRevisionId,
};

const OrderAttribute: TComponentKeys<'OrderAttribute'> = {
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  isBuiltIn: 'Wbudowany',
  isRequired: 'Wymagany',
  isVisible: Metadata.isHidden!,
  orderAttributeId: 'ID atrybutu',
  physicalDimension: Meaning.physicalDimensionId!,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  unitOfMeasureId: generalTerm.unit,
  valueType: 'Typ',
};

const OrderAttributeNumber: TComponentKeys<'OrderAttributeNumber'> = {
  ...OrderAttribute,
  minValue: 'Minimalna wartość',
  maxValue: 'Maksymalna wartość',
  isMaxExclusive: 'Wyłączne maksimum',
  isMinExclusive: 'Wyłączne minimum',
};

const OrderAttributeString: TComponentKeys<'OrderAttributeString'> = {
  ...OrderAttribute,
  minLength: 'Minimalna długość',
  maxLength: 'Maksymalna długość',
  pattern: 'Wzorzec',
};

const OrderAttributeValueType: Record<NonNullable<TComponentValue<'OrderAttribute'>['valueType']>, string> = {
  'string': OpenAPIPrimitiveType.string,
  'integer': OpenAPIPrimitiveType.integer,
  'number': OpenAPIPrimitiveType.number,
  'boolean': OpenAPIPrimitiveType.boolean,
  'date-time': 'Znacznik czasu',
};

const BuiltInOrderAttribute: Record<TComponentValue<'BuiltInOrderAttribute'>, string> = {
  DUE_DATE: 'Termin',
  PRIORITY: 'Priorytet',
  REMARK: 'Uwaga',
  START_NOT_BEFORE: 'Najwcześniejszy dozwolony start',
  WORK_ORDER_NUMBER: 'Numer zlecenia',
};

const OperationCalendarStateStart: TComponentKeys<'OperationCalendarStateStart'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  calendarEventType: OperationEventDetails.calendarEventType,
  startAtUtc: OperationEventDetails.startAtUtc,
  calendarEventTagId: CalendarEventTag.displayName,
};

const OperationCalendarStateFinish: TComponentKeys<'OperationCalendarStateFinish'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  finishedAtUtc: OperationEventDetails.endAtUtc,
};

const ScaleOptions: Record<EScaleOption, string> = {
  [EScaleOption.FIT_SCREEN]: 'Dopasuj ekran',
  [EScaleOption.FULL_SIZE]: '100%',
};

export default {
  AlarmCollection,
  AlarmDefinition,
  AlarmEvent,
  AlarmEventSeverity,
  AlarmSeverity,
  AlarmStatistics,
  AppGroupCommercial: NonTranslatable.AppGroupCommercial,
  AppGroupSemanticDepartment,
  AppGroupSemanticTopic,
  AppId,
  AssessmentTestResultType,
  Asset,
  AssetBlobIndex,
  AssetCreate,
  AssetHierarchyLevel,
  Weekday,
  WeekdayShort,
  AssetParameter,
  AssetParameterDefinition,
  AssetProfileCombinator,
  AssetProfileStatus,
  AssetState,
  AssetStatusJobInfo,
  AssetStatusOverview,
  AssetUtilization,
  AuditEnvelopStabilizationCombinationType,
  AuditEnvelope,
  AuditEnvelopeNonPassedReasons,
  AuditResultType,
  AuditSpecification,
  AuditSpecificationResultDetail,
  AuditSpecificationResultOverview,
  AuditTrailEvent,
  AuditTrailSettings,
  AutomatIdentifier,
  BlobMeaning,
  BuiltInOrderAttribute,
  CCTPhaseBoundaryType,
  CalendarEventType,
  CameraRegistryEntry,
  CameraRegistryEntryCameraType,
  CarburizingRecipeSimulationValuesType,
  CarburizingSimulationPartShape,
  CarburizingSimulationPartShapeSize,
  CarburizingSimulatorRecipeType,
  Chamber,
  ChamberCreate,
  ChamberType,
  ChartAssessmentTest,
  ChartAssessmentTestRequirementType,
  ChemicalElementInSteel,
  CompanyCreate,
  CounterAction,
  CsvDatetimeFormat,
  CsvLineTerminator,
  CsvQuoteCharacter,
  CsvQuoteStyle,
  CsvQuoteStyleHint,
  CsvSeperator,
  Customer,
  DataFormat,
  DataSource,
  DataSourceAssociation,
  DataSourceType,
  EnvelopeThreshold,
  EquipmentCreate,
  EquipmentPhysicalType,
  EquipmentProfile,
  EquipmentProfileMeaning,
  EquipmentTreeNavigation,
  ExpressionFunctionTypeAggregation,
  ExpressionNamedConstant,
  ExpressionOperatorType,
  ExpressionType,
  ExpressionUnaryOperatorType,
  FileAssessmentTest,
  HandlingState,
  HeatTreatmentCapability,
  HeatTreatmentProcessType,
  Job,
  JobStage,
  JobStatus,
  JobValueBaselineMetrics,
  JobConsumption,
  JobValueComparison,
  JobValueComparisonPeriod,
  JobValueData,
  ShiftHoursWindow,
  LocationShift,
  LocationCreate,
  LogFileState,
  MaintenanceEventDetails,
  MaintenanceEventOverview,
  MaintenanceEventType,
  MaintenancePeriod,
  MaintenancePriority,
  MaintenanceRecurrenceEndCondition,
  MaintenanceRecurrenceMonthDayDimension,
  MaintenanceRecurrenceMonthDayValue,
  MaintenanceSeriesCreate,
  MaintenanceState,
  MaintenanceStateType,
  MaintenanceTaskResultType,
  MaintenanceTaskReviewType,
  Material,
  MaterialCalculationModelElementCoefficient,
  MaterialCalculationModelOutput,
  MaterialGroup,
  MaterialNamingSystem,
  MaterialPropertySpectrum,
  Meaning,
  Metadata,
  MetadataUpdate,
  MetadataWithChartConfigInput,
  ModelBuilderModelOverview,
  ModelBuilderModelType,
  ModelBuilderTrainingRequest,
  ModelLifecycleState,
  NotificationChannelType,
  NotificationTable,
  NpmSystemIdentifier,
  Observation,
  ObservationType,
  ObservationTypeHeader,
  OpenAPIPrimitiveType,
  OperationCalendarStateFinish,
  OperationCalendarStateStart,
  CalendarEventTag,
  OperationEventDetails,
  OperationEventOverview,
  Order,
  OrderAttribute,
  OrderAttributeNumber,
  OrderAttributeString,
  OrderAttributeValueType,
  OrderStatus,
  PT9800RecipeSimulationInput,
  Part,
  PartAssetRecipeQualification,
  PartAttribute,
  PartDetails,
  PartInstance,
  OperationOrder,
  PartMaterialProperties,
  PhysicalDimension,
  ProductionStatistics,
  PtDatabaseIdentifier,
  QmulusDashboard,
  QmulusDashboardMixinData,
  QmulusDashboardMixinLimits,
  QmulusDashboardNamedLimit,
  QmulusDashboardThreshold,
  QmulusDashboardWidget,
  QmulusDashboardWidgetGauge,
  QmulusDashboardWidgetGaugeAppearance,
  QmulusDashboardWidgetObservations,
  QmulusDashboardWidgetObservationsVariant,
  QmulusDashboardWidgetType,
  QmulusDriver,
  QmulusLicenseViolationReason,
  QualityPreset,
  QuenchingMedia,
  ReportBlockAlarmsTemplateVariable,
  ReportBlockAuditEnvelopesTemplateVariable,
  ReportBlockAuditReportHeaderTemplateVariable,
  ReportBlockJobDetailsHeaderTemplateVariable,
  ReportBlockId,
  ReportBlockJobPartsField,
  ReportBlockJobPartsTemplateVariable,
  ReportBlockJobStagesTemplateVariable,
  ReportBlockQualityReportHeaderTemplateVariable,
  ReportBlockQualitySamplesTemplateVariable,
  ReportBlockRecipeTemplateVariable,
  ReportBlockSignaturesTemplateVariable,
  ReportComponentAuditEnvelopeParameter,
  ReportComponentAuditEnvelopeSectionsParameter,
  ReportComponentAuditResultParameter,
  ReportComponentFormatQuantityParameter,
  ReportComponentFormatValueParameter,
  ReportComponentIdType,
  ReportComponentIdTypeDescription,
  ReportComponentKeyValueGridParameter,
  ReportComponentKeyValuePairParameter,
  ReportComponentProthermBatchRecipeParameter,
  ReportComponentProthermBeltRecipeParameter,
  ReportComponentQualityChartTestParameter,
  ReportComponentQualityFileTestParameter,
  ReportComponentQualityImageTestParameter,
  ReportComponentQualitySampleParameter,
  ReportComponentQualityTableTestParameter,
  ReportComponentQualityTestParameter,
  ReportComponentQualityTestResultParameter,
  ReportComponentTableParameter,
  ReportComponentWarningParameter,
  ReportOverview,
  ReportScopeType,
  ReportTemplateIdType,
  ReportTemplateKind,
  ReportTemplateFooter,
  ReportTemplateHeader,
  ReportTemplateUpsert,
  Role,
  RoleWithPermissions,
  RootCause,
  RootCauseGroup,
  Sample,
  ScaleType,
  ScaleOptions,
  SimulationStep,
  SimulatorAMS275910CompoundLayerClass,
  SimulatorAMS275910SteelClass,
  SimulatorAMS275912PorosityClass,
  SimulatorAMS275912SteelClass,
  SimulatorPreTreatmentType,
  UnitOfMeasurementDisplayName,
  UnitOfMeasurementSymbol,
  SimulatorRecipe,
  SimulatorRecipeSetpoint,
  SimulatorRecipeSetpointType,
  SimulatorRecipeStep,
  SimulatorSimulationPart,
  SparePart,
  SubscriptionTreeNode,
  TableAssessmentTest,
  TableAssessmentTestItem,
  TableTestData,
  TaskInstance,
  TimeBucketType,
  TimeBucket_AssetUtilization_,
  User,
  WorkplaceIdentifier,
  expressionFunctionCategories,
  expressionFunctionsExplanations,

  util: {
    timeBucketName: 'Okres czasu',
  },

  meaningId: {
    ...TimeseriesMeaning,
    ...ConsumptionMeaning,
    ...JobFeatureMeaning,
    ...PhysicalPropertyMeaning,
  },
  npm: npmApiTranslations,
  pt9800: pt9800ApiTranslations,
};
